import { Container, Graphics, Rectangle, Sprite, Text, type Ticker } from 'pixi.js';
import { BaseScene, type SceneServices } from './BaseScene';
import { BackgroundManager, type BackgroundDebugState } from '../app/BackgroundManager';
import { CharacterManager, type CharacterManagerDebugState } from '../app/CharacterManager';
import { createEvidenceImageReference, findEvidenceDefinition } from '../app/EvidenceCatalog';
import type { GameConfig } from '../app/GameConfig';
import { resolveScenarioLineCharacters } from '../app/ScenarioCharacterResolver';
import type {
  BackgroundCue,
  EvidenceDefinition,
  ScenarioChoice,
  ScenarioDocument,
  ScenarioLine,
  ScenarioLineEffect,
  ScenarioLineEffectOptions,
  ScenarioNode,
  ScenarioResumePoint
} from '../app/types';
import type { ViewportLayout } from '../app/Viewport';
import { debugLogger } from '../systems/DebugLogger';
import { ScenarioRunner } from '../systems/ScenarioRunner';
import {
  executeScenarioAction,
  type FadeRequest,
  type ScenarioActionExecutionContext
} from '../systems/ScenarioActions';
import type { GameInputEvent, InputDebugState } from '../systems/InputManager';
import {
  BacklogModal,
  Button,
  ChoicePanel,
  HeaderBar,
  Modal,
  TextBox,
  Typewriter,
  UI_COLORS,
  UI_FONT_FAMILY,
  createTextLayoutDebugEntry,
  fitTextToBounds,
  type TextLayoutDebugState
} from '../ui';

export interface NovelSceneOptions {
  scenario: ScenarioDocument;
  initialNodeId?: string;
  onOpenEvidence?: () => void;
  onOpenCaseList?: () => void;
  onOpenSettings?: () => void;
  onGoToTitle?: () => void;
  onCompleteCase?: () => void | Promise<void>;
  onSaveProgress?: (mode: NovelSaveMode) => NovelSaveProgressResult;
}

export type NovelSaveMode = 'auto' | 'manual';

export interface NovelSaveProgressResult {
  status: 'saved' | 'skipped' | 'failed';
  slotId?: number;
  savedAt?: string;
  reason?: string;
}

const CHOICE_PANEL_WIDTH = 720;
const CHOICE_BUTTON_HEIGHT = 60;
const CHOICE_BUTTON_GAP = 14;
const CHOICE_PANEL_HEADER_HEIGHT = 48;
const CHOICE_PANEL_BOTTOM_MARGIN = 24;
const STAMP_DURATION_MS = 1280;
const STAMP_INPUT_GUARD_MS = 1040;
const STAMP_SETTLE_MS = 220;
const STAMP_FADE_MS = 320;
const KONAI_ARRIVAL_DURATION_MS = 920;
const KONAI_ARRIVAL_INPUT_GUARD_MS = 620;
const KONAI_ARRIVAL_SETTLE_MS = 220;
const KONAI_ARRIVAL_FADE_MS = 220;
const POLICE_CALL_DURATION_MS = 820;
const POLICE_CALL_INPUT_GUARD_MS = 560;
const POLICE_CALL_SETTLE_MS = 170;
const POLICE_CALL_FADE_MS = 180;
const THIEF_CUE_NOTICE_DURATION_MS = 980;
const THIEF_CUE_FAIL_DURATION_MS = 760;
const THIEF_CUE_INPUT_GUARD_MS = 520;
const THIEF_CUE_SETTLE_MS = 190;
const THIEF_CUE_FADE_MS = 190;
const CLOCK_REVERSE_DURATION_MS = 1180;
const CLOCK_REVERSE_INPUT_GUARD_MS = 680;
const CLOCK_REVERSE_SETTLE_MS = 240;
const CLOCK_REVERSE_FADE_MS = 260;
const CASE_FILE_TRANSITION_DURATION_MS = 980;
const CASE_FILE_TRANSITION_INPUT_GUARD_MS = 620;
const CASE_FILE_TRANSITION_SETTLE_MS = 220;
const CASE_FILE_TRANSITION_FADE_MS = 220;
const EVIDENCE_POPUP_DURATION_MS = 1900;
const EVIDENCE_POPUP_INPUT_GUARD_MS = 0;
const EVIDENCE_POPUP_SETTLE_MS = 260;
const EVIDENCE_POPUP_WIDTH = 320;
const EVIDENCE_POPUP_HEIGHT = 148;
const EVIDENCE_POPUP_IMAGE_WIDTH = 176;
const EVIDENCE_POPUP_IMAGE_HEIGHT = 90;
const EVIDENCE_POPUP_NAME_PLATE_X = 214;
const EVIDENCE_POPUP_NAME_PLATE_Y = 74;
const EVIDENCE_POPUP_NAME_PLATE_WIDTH = 88;
const EVIDENCE_POPUP_NAME_PLATE_HEIGHT = 42;
const EVIDENCE_POPUP_NAME_TEXT_PADDING_X = 8;
const EVIDENCE_POPUP_NAME_TEXT_PADDING_Y = 4;
const AUTO_BUTTON_WIDTH = 116;
const AUTO_BUTTON_HEIGHT = 42;
const AUTO_ADVANCE_MIN_DELAY_MS = 10000;
const AUTO_ADVANCE_MAX_DELAY_MS = 13200;
const NOVEL_EFFECT_INPUT_LOCK_REASON = 'novel_effect';
const INPUT_DEBUG_QUERY_PARAM = 'debugInput';
const FAST_EFFECT_QUERY_PARAM = 'fastEffects';
const SKIP_EFFECT_DURATION_SCALE = 0.18;
const TEST_EFFECT_DURATION_SCALE = 0.12;
const FAST_EFFECT_MIN_DURATION_MS = 64;
const FAST_EFFECT_MIN_INPUT_GUARD_MS = 72;
const FAST_EFFECT_MIN_SETTLE_MS = 48;
const FAST_EFFECT_MIN_FADE_MS = 48;
const FAST_EFFECT_MIN_SHAKE_MS = 40;

type ThiefCueKind = 'notice' | 'fail';
type EffectDurationOptionKey = 'duration' | 'inputGuard' | 'settle' | 'fade' | 'shakeDuration';
type LinePlaybackReason = 'normal' | 'readSkip' | 'testFast';
type PersistentLineEffectKind =
  | 'stamp'
  | 'konaiArrival'
  | 'policeCall'
  | 'thiefCue'
  | 'clockReverse'
  | 'caseFileTransition';

interface LinePlaybackMode {
  shortenEffects: boolean;
  suppressSe: boolean;
  revealTextInstantly: boolean;
  timingScale: number;
  reason: LinePlaybackReason;
}

type EffectDurationDefaults = Partial<Record<EffectDurationOptionKey, number>>;

export interface NovelPlaybackDebugState {
  fastEffects: boolean;
  suppressSe: boolean;
  revealTextInstantly: boolean;
  reason: LinePlaybackReason;
}

export interface NovelEffectDebugState {
  effectLayerChildCount: number;
  effectLayerAlpha: number;
  effectLayerX: number;
  effectLayerY: number;
  effectLayerScaleX: number;
  effectLayerScaleY: number;
  sceneX: number;
  sceneY: number;
  fadeLayerVisible: boolean;
  fadeOverlayAlpha: number;
  sceneFadeActive: boolean;
  activeTimers: {
    stamp: number;
    konaiArrival: number;
    policeCall: number;
    thiefCue: number;
    clockReverse: number;
    caseFileTransition: number;
    evidencePopup: number;
    shake: number;
  };
}

export interface NovelInputDebugState {
  blocked: boolean;
  reasons: string[];
  effectInputLockActive: boolean;
  guards: {
    transition: number;
    backgroundTransitioning: boolean;
    caseFileTransition: number;
    clockReverse: number;
    thiefCue: number;
    policeCall: number;
    konaiArrival: number;
    stamp: number;
    evidencePopup: number;
    fade: boolean;
    completingNode: boolean;
    choicePanelVisible: boolean;
    choicePanelLocked: boolean;
    choicePanelIntroComplete: boolean;
  };
  playback: NovelPlaybackDebugState;
  effects: NovelEffectDebugState;
  background: BackgroundDebugState;
  characters: CharacterManagerDebugState;
  input: InputDebugState;
}

interface SceneFadeState {
  direction: FadeRequest['direction'];
  color: number;
  startAlpha: number;
  elapsedMs: number;
  durationMs: number;
  resolve: () => void;
}

export class NovelScene extends BaseScene {
  private readonly scenario: ScenarioDocument;
  private readonly runner: ScenarioRunner;
  private readonly backgroundManager: BackgroundManager;
  private readonly characterManager: CharacterManager;
  private readonly backgroundLayer = new Container();
  private readonly characterLayer = new Container();
  private readonly effectLayer = new Container();
  private readonly evidencePopupLayer = new Container();
  private readonly uiLayer = new Container();
  private readonly fadeLayer = new Container();
  private readonly fadeOverlay = new Graphics();
  private readonly modalLayer = new Container();
  private readonly inputDebugText = new Text({
    text: '',
    style: {
      fill: UI_COLORS.gold,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 15,
      fontWeight: '800'
    }
  });
  private readonly showInputDebugOverlay = shouldShowInputDebugOverlay();
  private readonly textBox = new TextBox({ width: 1160, height: 190 });
  private readonly typewriter: Typewriter;
  private readonly choicePanel = new ChoicePanel({
    width: CHOICE_PANEL_WIDTH,
    choiceHeight: CHOICE_BUTTON_HEIGHT,
    gap: CHOICE_BUTTON_GAP,
    onSelect: (choice) => void this.selectChoice(choice)
  });
  private readonly headerBar = new HeaderBar({
    width: 620,
    actions: ['save', 'evidence', 'caseList', 'settings', 'title'],
    lockOnAction: true,
    onAction: (action) => this.handleHeaderAction(action)
  });
  private readonly backlogButton = new Button({
    label: 'ログ',
    width: 104,
    height: 42,
    variant: 'secondary',
    onPress: () => this.openBacklog()
  });
  private readonly autoButton = new Button({
    label: 'オートOFF',
    width: AUTO_BUTTON_WIDTH,
    height: AUTO_BUTTON_HEIGHT,
    variant: 'secondary',
    onPress: () => this.toggleAutoMode()
  });
  private readonly backlogModal = new BacklogModal({
    entries: [],
    onClose: () => this.closeBacklog()
  });
  private readonly nodeCaption = new Text({
    text: '',
    style: {
      fill: UI_COLORS.mutedInk,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 18,
      fontWeight: '700'
    }
  });

  private layout: ViewportLayout | null = null;
  private noticeModal: Modal | null = null;
  private activeLineEffectKind: PersistentLineEffectKind | null = null;
  private stampDisplay: Container | null = null;
  private stampTimerMs = 0;
  private stampInputGuardMs = 0;
  private stampElapsedMs = 0;
  private stampSettleMs = STAMP_SETTLE_MS;
  private konaiArrivalTimerMs = 0;
  private konaiArrivalInputGuardMs = 0;
  private konaiArrivalElapsedMs = 0;
  private konaiArrivalSettleMs = KONAI_ARRIVAL_SETTLE_MS;
  private policeCallTimerMs = 0;
  private policeCallInputGuardMs = 0;
  private policeCallElapsedMs = 0;
  private policeCallSettleMs = POLICE_CALL_SETTLE_MS;
  private thiefCueTimerMs = 0;
  private thiefCueInputGuardMs = 0;
  private thiefCueElapsedMs = 0;
  private thiefCueSettleMs = THIEF_CUE_SETTLE_MS;
  private thiefCueKind: ThiefCueKind | null = null;
  private clockReverseDisplay: Container | null = null;
  private clockReverseLongHand: Container | null = null;
  private clockReverseShortHand: Container | null = null;
  private clockReverseTimerMs = 0;
  private clockReverseInputGuardMs = 0;
  private clockReverseElapsedMs = 0;
  private clockReverseDurationMs = CLOCK_REVERSE_DURATION_MS;
  private clockReverseSettleMs = CLOCK_REVERSE_SETTLE_MS;
  private caseFileTransitionDisplay: Container | null = null;
  private caseFileTransitionTimerMs = 0;
  private caseFileTransitionInputGuardMs = 0;
  private caseFileTransitionElapsedMs = 0;
  private caseFileTransitionSettleMs = CASE_FILE_TRANSITION_SETTLE_MS;
  private evidencePopupDisplay: Container | null = null;
  private evidencePopupNameText: Text | null = null;
  private evidencePopupTimerMs = 0;
  private evidencePopupInputGuardMs = 0;
  private evidencePopupElapsedMs = 0;
  private evidencePopupSettleMs = EVIDENCE_POPUP_SETTLE_MS;
  private evidencePopupGeneration = 0;
  private transitionInputGuardMs = 0;
  private shakeTimerMs = 0;
  private shakeIntensity = 0;
  private sceneFade: SceneFadeState | null = null;
  private leavingScene = false;
  private completingNode = false;
  private effectInputLockActive = false;
  private autoModeEnabled = false;
  private autoAdvanceElapsedMs = 0;
  private autoAdvancePositionKey: string | null = null;
  private autoAdvanceInFlight = false;
  private lastAutoSavedPositionKey: string | null = null;
  private playbackDebugState = createNovelPlaybackDebugState(false, false, false, 'normal');
  private inputDebugState: NovelInputDebugState = createNovelInputDebugState(false, [], false, {
    transition: 0,
    backgroundTransitioning: false,
    caseFileTransition: 0,
    clockReverse: 0,
    thiefCue: 0,
    policeCall: 0,
    konaiArrival: 0,
    stamp: 0,
    evidencePopup: 0,
    fade: false,
    completingNode: false,
    choicePanelVisible: false,
    choicePanelLocked: false,
    choicePanelIntroComplete: true
  });

  public constructor(
    services: SceneServices,
    private readonly config: GameConfig,
    private readonly options: NovelSceneOptions
  ) {
    super(services);
    this.scenario = options.scenario;
    this.backgroundManager = new BackgroundManager({
      assetLoader: this.services.getAssets(),
      width: this.config.baseResolution.width,
      height: this.config.baseResolution.height
    });
    this.characterManager = new CharacterManager({
      assetLoader: this.services.getAssets(),
      width: this.config.baseResolution.width,
      height: this.config.baseResolution.height
    });
    this.runner = new ScenarioRunner(this.scenario, {
      initialNodeId: options.initialNodeId,
      resumePoint: this.services.getState().getResumePoint()
    });
    this.lastAutoSavedPositionKey = createAutoSavePositionKey(
      this.services.getState().getResumePoint()
    );
    this.typewriter = new Typewriter({
      textBox: this.textBox,
      textSpeed: this.services.getState().getSettings().textSpeed
    });
  }

  protected override onStart(): void {
    debugLogger.log('scenario', 'novel scene start', {
      scenarioId: this.scenario.id,
      caseId: this.scenario.caseId,
      runner: this.runner.getState()
    });
    this.backgroundLayer.addChild(this.backgroundManager.container);
    this.characterLayer.addChild(this.characterManager.container);
    this.container.addChild(this.backgroundLayer);
    this.container.addChild(this.characterLayer);
    this.container.addChild(this.effectLayer);
    this.container.addChild(this.evidencePopupLayer);
    this.container.addChild(this.uiLayer);
    this.container.addChild(this.fadeLayer);
    this.container.addChild(this.modalLayer);
    this.fadeLayer.addChild(this.fadeOverlay);
    this.fadeLayer.visible = false;
    this.uiLayer.addChild(this.nodeCaption);
    this.uiLayer.addChild(this.headerBar);
    this.uiLayer.addChild(this.autoButton);
    this.uiLayer.addChild(this.backlogButton);
    this.uiLayer.addChild(this.choicePanel);
    this.uiLayer.addChild(this.textBox);
    this.inputDebugText.visible = this.showInputDebugOverlay;
    this.uiLayer.addChild(this.inputDebugText);
    this.modalLayer.addChild(this.backlogModal);
    this.backlogModal.hide();

    this.addCleanup(this.services.getInput().subscribe((event) => void this.handleInput(event)));
    this.renderCurrentNode();
    this.resize(this.services.getLayout());
  }

  protected override onDestroy(): void {
    debugLogger.log('scenario', 'novel scene destroy', {
      scenarioId: this.scenario.id,
      runner: this.runner.getState()
    });
    this.releaseEffectInputLock();
    this.autoModeEnabled = false;
    this.autoAdvanceInFlight = false;
    this.backgroundManager.destroy();
    this.characterManager.destroy();
  }

  public override resize(layout: ViewportLayout): void {
    this.layout = layout;
    const topControlGap = 8;
    const topControlReservedWidth = AUTO_BUTTON_WIDTH + 104 + topControlGap * 2;
    const shouldStackTopControls = layout.safeArea.width < 600;
    const headerWidth = shouldStackTopControls
      ? Math.min(620, layout.safeArea.width)
      : Math.min(620, Math.max(328, layout.safeArea.width - topControlReservedWidth));
    this.headerBar.setSize(headerWidth);
    this.headerBar.position.set(
      layout.safeArea.x + layout.safeArea.width - headerWidth,
      layout.safeArea.y
    );
    const topButtonY = this.headerBar.y + Math.round((58 - AUTO_BUTTON_HEIGHT) / 2);
    if (shouldStackTopControls) {
      this.autoButton.position.set(layout.safeArea.x, this.headerBar.y + 62);
      this.backlogButton.position.set(
        this.autoButton.x + AUTO_BUTTON_WIDTH + topControlGap,
        this.autoButton.y
      );
    } else {
      this.autoButton.position.set(
        Math.max(layout.safeArea.x, this.headerBar.x - AUTO_BUTTON_WIDTH - 104 - topControlGap * 2),
        topButtonY
      );
      this.backlogButton.position.set(
        this.autoButton.x + AUTO_BUTTON_WIDTH + topControlGap,
        topButtonY
      );
    }
    this.textBox.position.set(layout.safeArea.x, layout.baseHeight - 222);
    this.textBox.setSize(layout.safeArea.width, 190);
    this.positionChoicePanel();
    this.backgroundManager.resize(layout.baseWidth, layout.baseHeight);
    this.characterManager.resize(layout.baseWidth, layout.baseHeight);
    this.fadeLayer.hitArea = new Rectangle(0, 0, layout.baseWidth, layout.baseHeight);
    this.inputDebugText.position.set(layout.safeArea.x + 8, layout.safeArea.y + 64);
    this.drawFadeOverlay(this.sceneFade?.color ?? UI_COLORS.black, this.fadeOverlay.alpha);
    this.backlogModal.setViewportSize(layout.baseWidth, layout.baseHeight);
    this.noticeModal?.setViewportSize(layout.baseWidth, layout.baseHeight);
  }

  public override update(ticker: Ticker): void {
    const deltaMs = ticker.deltaMS;
    this.typewriter.setTextSpeed(this.services.getState().getSettings().textSpeed);
    this.typewriter.update(deltaMs);
    this.textBox.update(deltaMs);
    this.choicePanel.update(deltaMs);
    this.backgroundManager.update(deltaMs);
    this.characterManager.update(deltaMs);
    this.updateEffects(deltaMs);
    this.updateInputBlockState();
    this.updateAutoAdvance(deltaMs);
  }

  private renderCurrentNode(): void {
    const node = this.runner.getCurrentNode();
    debugLogger.log('scenario', 'render node', {
      runner: this.runner.getState(),
      background: node.background,
      bgm: node.bgm,
      characterIds: node.characters?.map((character) => character.id) ?? [],
      choiceCount: node.choices?.length ?? 0,
      actionCount: node.actions?.length ?? 0
    });
    this.choicePanel.hide();
    const backgroundCue = this.resolveBackgroundCue(node.background);
    this.guardInputForBackgroundTransition(backgroundCue);
    void this.backgroundManager.setBackground(backgroundCue);
    void this.characterManager.setCharacters(node.characters ?? []);
    this.nodeCaption.text = `${getCaseLabel(this.scenario.caseId)} / ${this.scenario.title}`;
    this.playNodeBgm(node);
    this.showCurrentLine();
    this.updateInputBlockState();
  }

  private showCurrentLine(): void {
    const node = this.runner.getCurrentNode();
    const line = this.runner.getCurrentLine();
    const alreadyRead = this.services.getState().hasReadLine(this.runner.getCurrentPosition());
    const playbackMode = this.createLinePlaybackMode(alreadyRead);
    debugLogger.log('scenario', 'show line', {
      runner: this.runner.getState(),
      lineId: line.id ?? null,
      speaker: line.speaker ?? null,
      speakerId: line.speakerId ?? null,
      textLength: line.text.length,
      se: line.se ?? null,
      voice: line.voice ?? null,
      effect: line.effect ?? null,
      alreadyRead,
      playback: playbackMode
    });
    this.playbackDebugState = createNovelPlaybackDebugState(
      playbackMode.shortenEffects,
      playbackMode.suppressSe,
      playbackMode.revealTextInstantly,
      playbackMode.reason
    );

    this.textBox.setSpeaker(line.speaker ?? '');
    this.textBox.setHighlights(line.highlights ?? []);
    this.textBox.prepareTextLayout(line.text, line.ruby ?? []);
    this.textBox.show();
    void this.characterManager.setCharacters(resolveScenarioLineCharacters(line, node));
    this.typewriter.start(line.text);
    if (playbackMode.revealTextInstantly) {
      this.typewriter.revealAll();
    }
    this.resetAutoAdvanceTimer();
    this.recordLine(line);
    this.applyLineCue(line, playbackMode);
    this.updateInputBlockState();
  }

  public getInputDebugState(): NovelInputDebugState {
    return cloneNovelInputDebugState(this.inputDebugState);
  }

  public async setBackgroundForDebug(cue: BackgroundCue): Promise<BackgroundDebugState> {
    const backgroundCue = this.resolveBackgroundCue(cue);
    this.guardInputForBackgroundTransition(backgroundCue);
    await this.backgroundManager.setBackground(backgroundCue);
    this.updateInputBlockState();
    return this.backgroundManager.getDebugState();
  }

  public override getTextLayoutDebugState(): TextLayoutDebugState {
    return {
      scene: 'novel',
	      entries: [
	        ...this.textBox.getTextLayoutDebugEntries('novel.textBox'),
	        ...this.choicePanel.getTextLayoutDebugEntries('novel.choices'),
	        ...this.getEvidencePopupTextLayoutDebugEntries()
	      ]
	    };
	  }

  private async advance(options: { continueAfterDismiss?: boolean } = {}): Promise<void> {
    if (this.noticeModal || this.backlogModal.visible) {
      debugLogger.log('scenario', 'advance ignored by modal', {
        noticeVisible: this.noticeModal !== null,
        backlogVisible: this.backlogModal.visible
      });
      return;
    }

    if (this.dismissPersistentEffectDisplays()) {
      debugLogger.log('scenario', 'advance dismissed persistent effect display', {
        runner: this.runner.getState(),
        continueAfterDismiss: options.continueAfterDismiss === true
      });
      if (!options.continueAfterDismiss) {
        return;
      }
    }

    if (this.isInputBlocked()) {
      debugLogger.log('scenario', 'advance blocked', this.inputDebugState);
      return;
    }

    if (this.typewriter.handleAdvance() === 'revealed') {
      debugLogger.log('scenario', 'advance revealed current line', {
        runner: this.runner.getState()
      });
      return;
    }

    if (this.choicePanel.visible) {
      debugLogger.log('scenario', 'advance ignored while choices visible', {
        runner: this.runner.getState(),
        choicePanel: this.choicePanel.getState()
      });
      return;
    }

    if (this.runner.canAdvanceLine()) {
      this.runner.advanceLine();
      debugLogger.log('scenario', 'advance line', {
        runner: this.runner.getState()
      });
      this.showCurrentLine();
      return;
    }

    if (this.completingNode) {
      return;
    }

    this.completingNode = true;
    this.updateInputBlockState();
    try {
      await this.completeCurrentNode();
    } finally {
      this.completingNode = false;
      if (!this.leavingScene) {
        this.updateInputBlockState();
      }
    }
  }

  private toggleAutoMode(): void {
    this.setAutoModeEnabled(!this.autoModeEnabled);
  }

  private setAutoModeEnabled(enabled: boolean): void {
    if (this.autoModeEnabled === enabled) {
      return;
    }

    this.autoModeEnabled = enabled;
    this.resetAutoAdvanceTimer();
    this.autoButton.setLabel(enabled ? 'オートON' : 'オートOFF');
    this.autoButton.setVariant(enabled ? 'primary' : 'secondary');
    debugLogger.log('scenario', 'auto mode changed', {
      enabled,
      runner: this.runner.getState()
    });
  }

  private updateAutoAdvance(deltaMs: number): void {
    if (!this.autoModeEnabled || this.autoAdvanceInFlight || this.leavingScene) {
      return;
    }

    if (!this.canAutoAdvance()) {
      this.resetAutoAdvanceTimer();
      return;
    }

    const positionKey = createAutoSavePositionKey(this.runner.getCurrentPosition());
    if (this.autoAdvancePositionKey !== positionKey) {
      this.autoAdvancePositionKey = positionKey;
      this.autoAdvanceElapsedMs = 0;
    }

    this.autoAdvanceElapsedMs += Math.max(0, deltaMs);
    if (this.autoAdvanceElapsedMs < this.getAutoAdvanceDelayMs()) {
      return;
    }

    this.autoAdvanceInFlight = true;
    this.autoAdvanceElapsedMs = 0;
    void this.advance({ continueAfterDismiss: true }).finally(() => {
      this.autoAdvanceInFlight = false;
      this.resetAutoAdvanceTimer();
    });
  }

  private canAutoAdvance(): boolean {
    if (
      this.noticeModal ||
      this.backlogModal.visible ||
      this.choicePanel.visible ||
      this.isInputBlocked() ||
      !this.typewriter.isComplete()
    ) {
      return false;
    }

    return true;
  }

  private resetAutoAdvanceTimer(): void {
    this.autoAdvanceElapsedMs = 0;
    this.autoAdvancePositionKey = null;
  }

  private getAutoAdvanceDelayMs(): number {
    const speed = clampNumber(this.services.getState().getSettings().autoSpeed, 0, 1);
    return Math.round(
      AUTO_ADVANCE_MAX_DELAY_MS -
        (AUTO_ADVANCE_MAX_DELAY_MS - AUTO_ADVANCE_MIN_DELAY_MS) * speed
    );
  }

  private dismissPersistentEffectDisplays(): boolean {
    let dismissed = false;

    if (this.hasDismissibleLineEffectDisplay() && this.getActiveLineEffectInputGuardMs() <= 0) {
      this.resetLineEffectLayer();
      dismissed = true;
    }

    if (this.evidencePopupDisplay && this.evidencePopupInputGuardMs <= 0) {
      this.destroyEvidencePopupDisplay();
      dismissed = true;
    }

    if (dismissed) {
      this.updateInputBlockState();
    }

    return dismissed;
  }

  private hasDismissibleLineEffectDisplay(): boolean {
    return this.activeLineEffectKind !== null && this.effectLayer.children.length > 0;
  }

  private getActiveLineEffectInputGuardMs(): number {
    switch (this.activeLineEffectKind) {
      case 'stamp':
        return this.stampInputGuardMs;
      case 'konaiArrival':
        return this.konaiArrivalInputGuardMs;
      case 'policeCall':
        return this.policeCallInputGuardMs;
      case 'thiefCue':
        return this.thiefCueInputGuardMs;
      case 'clockReverse':
        return this.clockReverseInputGuardMs;
      case 'caseFileTransition':
        return this.caseFileTransitionInputGuardMs;
      case null:
        return 0;
    }
  }

  private async completeCurrentNode(): Promise<void> {
    debugLogger.log('scenario', 'complete node', {
      runner: this.runner.getState()
    });
    await this.executeNodeActionsOnce();
    if (this.leavingScene) {
      return;
    }

    const choices = this.runner.getChoices();
    if (choices?.length) {
      debugLogger.log('scenario', 'show choices', {
        runner: this.runner.getState(),
        choices: choices.map((choice) => ({
          id: choice.id ?? null,
          next: choice.next,
          text: choice.text
        }))
      });
      this.choicePanel.setChoices(choices);
      this.positionChoicePanel(choices.length);
      return;
    }

    if (this.runner.moveToNextNode()) {
      debugLogger.log('scenario', 'move to next node', {
        runner: this.runner.getState()
      });
      this.renderCurrentNode();
      return;
    }

    this.showNotice('進行終了', 'このシナリオはここで終了です。');
  }

  private async selectChoice(choice: ScenarioChoice): Promise<void> {
    debugLogger.log('scenario', 'select choice', {
      runner: this.runner.getState(),
      choiceId: choice.id ?? null,
      next: choice.next,
      text: choice.text,
      actionCount: choice.actions?.length ?? 0
    });
    void this.services.getAudio().playSe('choice_select');
    await this.executeChoiceActions(choice);
    if (this.leavingScene) {
      return;
    }

    this.runner.chooseChoice(choice);
    debugLogger.log('scenario', 'choice entered node', {
      runner: this.runner.getState()
    });
    this.renderCurrentNode();
  }

  private async executeNodeActionsOnce(): Promise<void> {
    await this.executeActions(this.runner.consumeNodeActions());
  }

  private async executeChoiceActions(choice: ScenarioChoice): Promise<void> {
    await this.executeActions(choice.actions);
  }

  private async executeActions(actions: readonly unknown[] | undefined): Promise<void> {
    if (!actions?.length) {
      return;
    }

    const context = this.createActionContext();
    let executedAction = false;
    for (const action of actions) {
      const result = await executeScenarioAction(action, context);
      executedAction = true;
      debugLogger.log('scenario', 'execute action', {
        runner: this.runner.getState(),
        result,
        action
      });
      if (this.leavingScene) {
        return;
      }
    }

    if (executedAction) {
      this.autoSaveProgress('actions');
    }
  }

  private createActionContext(): ScenarioActionExecutionContext {
    return {
      gameState: this.services.getState(),
      audio: this.services.getAudio(),
      onEvidenceAcquired: (evidenceId) => this.showEvidenceAcquired(evidenceId),
      onScreenShake: (request) => this.startShake(request.intensity, request.duration),
      onFade: (request) => this.startSceneFade(request),
      onGoToTitle: () => this.completeCaseAndContinue(),
      warn: (warning) => {
        console.warn(`[NovelScene] ${warning.reason}`, warning.action);
      }
    };
  }

  private recordLine(line: ScenarioLine): void {
    const position = this.runner.getCurrentPosition();
    this.services.getState().setResumePoint(position);
    if (!this.services.getState().hasReadLine(position)) {
      this.services.getState().appendBacklogLine(position, line);
      this.services.getState().markLineRead(position);
      this.backlogModal.appendEntry(this.services.getState().getBacklog().at(-1)!);
      debugLogger.log('scenario', 'record unread line', {
        position,
        backlogCount: this.services.getState().getBacklog().length
      });
      this.autoSaveProgress('line', position);
      return;
    }

    debugLogger.log('scenario', 'record already read line', { position });
    this.autoSaveProgress('line', position);
  }

  private autoSaveProgress(reason: 'line' | 'actions', position?: ScenarioResumePoint): void {
    if (!this.options.onSaveProgress || this.leavingScene) {
      return;
    }

    if (reason === 'line') {
      const saveKey = createAutoSavePositionKey(
        position ?? this.services.getState().getResumePoint()
      );
      if (this.lastAutoSavedPositionKey === saveKey) {
        return;
      }
      this.lastAutoSavedPositionKey = saveKey;
    }

    const result = this.options.onSaveProgress('auto');
    debugLogger.log('save', 'novel autosave progress', {
      reason,
      status: result.status,
      slotId: result.slotId ?? null,
      savedAt: result.savedAt ?? null,
      resultReason: result.reason ?? null
    });
  }

  private saveProgressManually(): void {
    const result = this.options.onSaveProgress?.('manual') ?? {
      status: 'skipped',
      reason: 'saveUnavailable'
    };

    debugLogger.log('save', 'novel manual save progress', {
      status: result.status,
      slotId: result.slotId ?? null,
      savedAt: result.savedAt ?? null,
      reason: result.reason ?? null
    });

    if (result.status === 'saved') {
      this.showNotice(
        '保存しました',
        `Slot ${result.slotId ?? '-'} に現在の進行を保存しました。\n${formatSavedAt(result.savedAt)}`
      );
      return;
    }

    this.showNotice(
      '保存できません',
      result.reason === 'noActiveSlot'
        ? '現在の保存先がありません。タイトルから「はじめから」または「つづきから」で事件ファイルを選んでください。'
        : '現在の進行を保存できませんでした。少し進めてからもう一度お試しください。'
    );
  }

  private createLinePlaybackMode(alreadyRead: boolean): LinePlaybackMode {
    const settings = this.services.getState().getSettings();
    const readSkip = alreadyRead && settings.skipReadText;
    const testFastEffects = shouldUseFastEffectMode();

    return {
      shortenEffects: readSkip || testFastEffects,
      suppressSe: readSkip,
      revealTextInstantly: readSkip,
      timingScale: readSkip
        ? Math.min(SKIP_EFFECT_DURATION_SCALE, testFastEffects ? TEST_EFFECT_DURATION_SCALE : 1)
        : TEST_EFFECT_DURATION_SCALE,
      reason: readSkip ? 'readSkip' : testFastEffects ? 'testFast' : 'normal'
    };
  }

  private applyLineCue(line: ScenarioLine, playbackMode: LinePlaybackMode): void {
    const effectOptions = this.resolveLineEffectOptions(
      line.effect,
      line.effectOptions,
      playbackMode
    );
    const shouldLineSePlayWithEffect = line.effect !== 'stamp';
    if (line.se && shouldLineSePlayWithEffect && !playbackMode.suppressSe) {
      void this.services.getAudio().playSe(line.se);
    }

    switch (line.effect) {
      case undefined:
      case 'none':
        return;
      case 'shake':
        this.startShake(
          resolveEffectIntensity(effectOptions?.shakeIntensity, 0.35),
          resolveEffectDuration(effectOptions?.shakeDuration, 260)
        );
        return;
      case 'stamp':
        if (line.se && !playbackMode.suppressSe) {
          void this.services.getAudio().playSe(line.se);
        }
        this.showStamp('未解決', effectOptions);
        this.syncReduceMotionLineEffectState();
        return;
      case 'flash':
        this.showStamp(effectOptions?.label ?? '!', effectOptions);
        this.syncReduceMotionLineEffectState();
        return;
      case 'konaiArrival':
        this.showKonaiArrival(effectOptions);
        this.syncReduceMotionLineEffectState();
        return;
      case 'policeCall':
        this.showPoliceCallCue(effectOptions);
        this.syncReduceMotionLineEffectState();
        return;
      case 'clockReverse':
        this.showClockReverseCue(effectOptions);
        this.syncReduceMotionLineEffectState();
        return;
      case 'caseFileTransition':
        this.showCaseFileTransitionCue(effectOptions);
        this.syncReduceMotionLineEffectState();
        return;
      case 'thiefNotice':
        this.showThiefNoticeCue(effectOptions);
        this.syncReduceMotionLineEffectState();
        return;
      case 'thiefFail':
        this.showThiefFailCue(effectOptions);
        this.syncReduceMotionLineEffectState();
        return;
      case 'glitch':
      case 'noise':
      default:
        this.warnUnsupportedLineEffect(String(line.effect), line);
    }
  }

  private resolveLineEffectOptions(
    effect: ScenarioLineEffect | undefined,
    options: ScenarioLineEffectOptions | undefined,
    playbackMode: LinePlaybackMode
  ): ScenarioLineEffectOptions | undefined {
    const adjusted: ScenarioLineEffectOptions = { ...(options ?? {}) };
    const defaults = getEffectDurationDefaults(effect);

    if (playbackMode.shortenEffects) {
      for (const key of Object.keys(defaults) as EffectDurationOptionKey[]) {
        const fallback = defaults[key];
        if (fallback === undefined) {
          continue;
        }

        adjusted[key] = scaleEffectDuration(
          resolveEffectDuration(options?.[key], fallback),
          key,
          playbackMode.timingScale
        );
      }
    }

    if (!this.isReduceMotionEnabled()) {
      return options === undefined && !playbackMode.shortenEffects ? undefined : adjusted;
    }

    return {
      ...adjusted,
      shakeIntensity: 0,
      shakeDuration: 0
    };
  }

  private warnUnsupportedLineEffect(effect: string, line: ScenarioLine): void {
    debugLogger.log('scenario', 'unsupported line effect', {
      effect,
      lineId: line.id ?? null,
      speakerId: line.speakerId ?? null
    });
    console.warn(`[NovelScene] Unsupported line effect "${effect}" was ignored.`, {
      lineId: line.id,
      speakerId: line.speakerId
    });
  }

  private playNodeBgm(node: ScenarioNode): void {
    if (node.bgm === undefined) {
      debugLogger.log('scenario', 'node bgm unchanged', { nodeId: node.id });
      return;
    }

    if (node.bgm === null) {
      debugLogger.log('scenario', 'node bgm stop', { nodeId: node.id });
      void this.services.getAudio().stopBgm();
      return;
    }

    debugLogger.log('scenario', 'node bgm play', { nodeId: node.id, bgm: node.bgm });
    void this.services.getAudio().playBgm(node.bgm, { fadeMs: 500, loop: true });
  }

  private resolveBackgroundCue(background: BackgroundCue | undefined): BackgroundCue | undefined {
    if (!background) {
      return background;
    }

    const settings = this.services.getState().getSettings();
    if (!settings.visualEffects) {
      return {
        ...background,
        effect: 'none'
      };
    }

    if (!settings.screenShake && background.effect === 'shake') {
      return {
        ...background,
        effect: 'none'
      };
    }

    if (settings.reduceMotion) {
      const reducedBackground = reduceBackgroundMotion(background);
      return settings.lightweightEffects
        ? applyLightweightBackgroundProfile(reducedBackground)
        : reducedBackground;
    }

    return settings.lightweightEffects ? applyLightweightBackgroundProfile(background) : background;
  }

  private guardInputForBackgroundTransition(background: BackgroundCue | undefined): void {
    const transition = background?.transition ?? 'cut';
    const durationMs = Math.max(0, background?.duration ?? 0);
    if (transition === 'cut' || transition === 'none' || durationMs <= 0) {
      return;
    }

    this.transitionInputGuardMs = Math.max(this.transitionInputGuardMs, durationMs);
    debugLogger.log('scenario', 'background transition input guard', {
      backgroundId: background?.id ?? null,
      transition,
      durationMs,
      transitionInputGuardMs: this.transitionInputGuardMs
    });
  }

  private startShake(intensity: number, duration: number): void {
    if (!this.services.getState().getSettings().screenShake || this.isReduceMotionEnabled()) {
      return;
    }

    this.shakeIntensity = Math.max(0, Math.min(intensity, 1));
    this.shakeTimerMs = Math.max(0, duration);
    debugLogger.log('scenario', 'start shake', {
      intensity: this.shakeIntensity,
      duration: this.shakeTimerMs
    });
  }

  private isReduceMotionEnabled(): boolean {
    return this.services.getState().getSettings().reduceMotion;
  }

  private syncReduceMotionLineEffectState(): void {
    if (this.isReduceMotionEnabled()) {
      this.updateEffects(0);
    }
  }

  private startSceneFade(request: FadeRequest): Promise<void> {
    this.sceneFade?.resolve();
    const durationMs = Math.max(0, request.duration);
    const color = parseFadeColor(request.color);
    const currentAlpha = this.fadeLayer.visible ? this.fadeOverlay.alpha : 0;
    const initialAlpha = getInitialSceneFadeAlpha(request.direction, currentAlpha);

    return new Promise((resolve) => {
      this.fadeOverlay.alpha = initialAlpha;
      this.fadeLayer.visible = true;
      this.sceneFade = {
        direction: request.direction,
        color,
        startAlpha: initialAlpha,
        elapsedMs: 0,
        durationMs,
        resolve
      };
      this.drawFadeOverlay(color, initialAlpha);
      this.updateInputBlockState();

      if (durationMs <= 0) {
        this.finishSceneFade();
      }
    });
  }

  private async showEvidenceAcquired(evidenceId: string): Promise<void> {
    const evidence = findEvidenceDefinition(evidenceId);
    if (!evidence) {
      return;
    }

    this.destroyEvidencePopupDisplay();
    const generation = ++this.evidencePopupGeneration;
    const image = await this.services.getAssets().loadImage(createEvidenceImageReference(evidence));
    if (this.leavingScene || generation !== this.evidencePopupGeneration) {
      return;
    }

    const layout = this.layout ?? this.services.getLayout();
    const root = this.createEvidencePopupCard(evidence, image.texture);
    const position = this.resolveEvidencePopupPosition(layout);
    root.pivot.set(EVIDENCE_POPUP_WIDTH / 2, EVIDENCE_POPUP_HEIGHT / 2);
    root.position.set(
      position.x + EVIDENCE_POPUP_WIDTH / 2,
      position.y + EVIDENCE_POPUP_HEIGHT / 2
    );
    root.alpha = 0;
    root.scale.set(this.isReduceMotionEnabled() ? 1 : 0.94);

    this.evidencePopupLayer.addChild(root);
    this.evidencePopupDisplay = root;
    this.evidencePopupTimerMs = EVIDENCE_POPUP_DURATION_MS;
    this.evidencePopupInputGuardMs = EVIDENCE_POPUP_INPUT_GUARD_MS;
    this.evidencePopupElapsedMs = 0;
    this.evidencePopupSettleMs = EVIDENCE_POPUP_SETTLE_MS;
    this.updateInputBlockState();
    void this.services.getAudio().playSe('evidence_get');
  }

  private createEvidencePopupCard(
    evidence: EvidenceDefinition,
    texture: Sprite['texture']
  ): Container {
    const root = new Container();

    const shadow = new Graphics();
    shadow
      .roundRect(8, 10, EVIDENCE_POPUP_WIDTH, EVIDENCE_POPUP_HEIGHT, 8)
      .fill({ color: UI_COLORS.black, alpha: 0.46 });
    root.addChild(shadow);

    const panel = new Graphics();
    panel
      .roundRect(0, 0, EVIDENCE_POPUP_WIDTH, EVIDENCE_POPUP_HEIGHT, 8)
      .fill({ color: UI_COLORS.black, alpha: 0.9 })
      .stroke({ color: UI_COLORS.gold, width: 3, alpha: 0.92 });
    panel.rect(0, 0, EVIDENCE_POPUP_WIDTH, 8).fill({ color: UI_COLORS.red, alpha: 0.9 });
    panel.rect(18, 40, EVIDENCE_POPUP_WIDTH - 36, 2).fill({ color: UI_COLORS.gold, alpha: 0.48 });
    root.addChild(panel);

    const titleText = new Text({
      text: '証拠入手',
      style: {
        fill: UI_COLORS.gold,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 22,
        fontWeight: '900'
      }
    });
    titleText.position.set(20, 13);
    root.addChild(titleText);

    const imageFrame = new Graphics();
    imageFrame
      .roundRect(20, 48, EVIDENCE_POPUP_IMAGE_WIDTH + 12, EVIDENCE_POPUP_IMAGE_HEIGHT + 12, 6)
      .fill({ color: UI_COLORS.paperLight, alpha: 0.16 })
      .stroke({ color: UI_COLORS.paperLight, width: 2, alpha: 0.82 });
    root.addChild(imageFrame);

    const sprite = new Sprite(texture);
    fitSpriteToBox(sprite, EVIDENCE_POPUP_IMAGE_WIDTH, EVIDENCE_POPUP_IMAGE_HEIGHT);
    sprite.position.set(
      26 + (EVIDENCE_POPUP_IMAGE_WIDTH - sprite.width) / 2,
      54 + (EVIDENCE_POPUP_IMAGE_HEIGHT - sprite.height) / 2
    );
    root.addChild(sprite);

	    const namePlate = new Graphics();
	    namePlate
	      .roundRect(
	        EVIDENCE_POPUP_NAME_PLATE_X,
	        EVIDENCE_POPUP_NAME_PLATE_Y,
	        EVIDENCE_POPUP_NAME_PLATE_WIDTH,
	        EVIDENCE_POPUP_NAME_PLATE_HEIGHT,
	        5
	      )
	      .fill({ color: UI_COLORS.gold, alpha: 0.92 });
	    root.addChild(namePlate);

		    const nameText = new Text({
		      text: evidence.shortName ?? evidence.name,
		      style: {
	        fill: UI_COLORS.black,
	        fontFamily: UI_FONT_FAMILY,
	        fontSize: 16,
	        lineHeight: 18,
	        fontWeight: '900',
	        wordWrap: true,
	        breakWords: true,
	        wordWrapWidth:
	          EVIDENCE_POPUP_NAME_PLATE_WIDTH - EVIDENCE_POPUP_NAME_TEXT_PADDING_X * 2
	      }
	    });
	    fitTextToBounds(nameText, {
	      maxWidth: EVIDENCE_POPUP_NAME_PLATE_WIDTH - EVIDENCE_POPUP_NAME_TEXT_PADDING_X * 2,
	      maxHeight: EVIDENCE_POPUP_NAME_PLATE_HEIGHT - EVIDENCE_POPUP_NAME_TEXT_PADDING_Y * 2,
	      baseFontSize: 16,
	      minFontSize: 11,
	      baseLineHeight: 18,
	      minLineHeight: 13,
	      wordWrap: true,
	      breakWords: true
	    });
	    nameText.position.set(
	      EVIDENCE_POPUP_NAME_PLATE_X + EVIDENCE_POPUP_NAME_TEXT_PADDING_X,
	      EVIDENCE_POPUP_NAME_PLATE_Y +
	        Math.max(0, (EVIDENCE_POPUP_NAME_PLATE_HEIGHT - nameText.height) / 2)
	    );
	    this.evidencePopupNameText = nameText;
	    root.addChild(nameText);

		    return root;
	  }

	  private getEvidencePopupTextLayoutDebugEntries(): TextLayoutDebugState['entries'] {
	    if (!this.evidencePopupNameText) {
	      return [];
	    }

	    return [
	      createTextLayoutDebugEntry(
	        'novel.evidencePopup.name',
	        this.evidencePopupNameText,
	        EVIDENCE_POPUP_NAME_PLATE_WIDTH - EVIDENCE_POPUP_NAME_TEXT_PADDING_X * 2,
	        EVIDENCE_POPUP_NAME_PLATE_HEIGHT - EVIDENCE_POPUP_NAME_TEXT_PADDING_Y * 2
	      )
	    ];
	  }

  private resolveEvidencePopupPosition(layout: ViewportLayout): { x: number; y: number } {
    const textBoxTop = this.textBox.y || layout.baseHeight - 222;
    const minY = layout.safeArea.y + 104;
    const maxY = Math.max(minY, textBoxTop - EVIDENCE_POPUP_HEIGHT - 28);
    const y = clampNumber(layout.safeArea.y + 108, minY, maxY);
    const margin = 46;
    const minX = layout.safeArea.x + margin;
    const maxX = Math.max(
      minX,
      layout.safeArea.x + layout.safeArea.width - EVIDENCE_POPUP_WIDTH - margin
    );
    const currentPositions = this.getActiveCharacterPositions();
    const nextPositions = this.getNextNodeCharacterPositions();
    const candidates = [
      { position: 'right', x: maxX },
      { position: 'left', x: minX },
      {
        position: 'center',
        x: layout.safeArea.x + (layout.safeArea.width - EVIDENCE_POPUP_WIDTH) / 2
      }
    ] as const;
    const candidate =
      candidates.find(
        (item) => !currentPositions.has(item.position) && !nextPositions.has(item.position)
      ) ??
      candidates.find((item) => !nextPositions.has(item.position)) ??
      candidates.find((item) => !currentPositions.has(item.position)) ??
      candidates[1];

    return {
      x: clampNumber(candidate.x, minX, maxX),
      y
    };
  }

  private getActiveCharacterPositions(): Set<'left' | 'center' | 'right'> {
    const node = this.runner.getCurrentNode();
    const line = this.runner.getCurrentLine();
    return new Set(
      (line.characters ?? node.characters ?? []).map((character) => character.position)
    );
  }

  private getNextNodeCharacterPositions(): Set<'left' | 'center' | 'right'> {
    const node = this.runner.getCurrentNode();
    if (!node.next) {
      return new Set();
    }

    const nextNode = this.scenario.nodes.find((candidate) => candidate.id === node.next);
    if (!nextNode) {
      return new Set();
    }

    const firstLine = nextNode.lines[0];
    return new Set(
      (firstLine?.characters ?? nextNode.characters ?? []).map((character) => character.position)
    );
  }

	  private destroyEvidencePopupDisplay(): void {
	    destroyChildren(this.evidencePopupLayer);
	    this.evidencePopupDisplay = null;
	    this.evidencePopupNameText = null;
	    this.evidencePopupTimerMs = 0;
    this.evidencePopupInputGuardMs = 0;
    this.evidencePopupElapsedMs = 0;
    this.evidencePopupSettleMs = EVIDENCE_POPUP_SETTLE_MS;
  }

  private showStamp(text: string, options?: ScenarioLineEffectOptions): void {
    this.resetLineEffectLayer();

    const centerX = this.config.baseResolution.width / 2;
    const centerY = this.config.baseResolution.height / 2 - 76;
    const label = options?.label ?? getStampLabel(text);
    const root = new Container();
    root.sortableChildren = true;
    this.effectLayer.addChild(root);

    const wash = new Graphics();
    wash
      .rect(0, 0, this.config.baseResolution.width, this.config.baseResolution.height)
      .fill({ color: UI_COLORS.redDark, alpha: 0.16 });
    wash.zIndex = 0;
    root.addChild(wash);

    const fileCard = new Container();
    fileCard.position.set(centerX, centerY + 4);
    fileCard.rotation = -0.025;
    fileCard.zIndex = 1;

    const file = new Graphics();
    file
      .roundRect(-356, -150, 712, 300, 8)
      .fill({ color: UI_COLORS.black, alpha: 0.86 })
      .stroke({ color: UI_COLORS.gold, width: 3, alpha: 0.86 });
    file.rect(-356, -150, 712, 42).fill({ color: UI_COLORS.redDark, alpha: 0.58 });
    file.rect(-318, -82, 636, 3).fill({ color: UI_COLORS.gold, alpha: 0.32 });
    file.rect(-318, 86, 636, 4).fill({ color: UI_COLORS.red, alpha: 0.34 });
    fileCard.addChild(file);

    const fileTitle = new Text({
      text: '事件記録ファイル',
      style: {
        fill: UI_COLORS.gold,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 24,
        fontWeight: '900'
      }
    });
    fileTitle.anchor.set(0.5);
    fileTitle.position.set(0, -126);
    fileCard.addChild(fileTitle);

    const fileCase = new Text({
      text: 'Case 0  消えた宝石商の娘',
      style: {
        fill: UI_COLORS.ink,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 28,
        fontWeight: '900'
      }
    });
    fileCase.anchor.set(0.5);
    fileCase.position.set(0, -56);
    fileCard.addChild(fileCase);

    const fileStatus = new Text({
      text: '公的ステータス: 未解決',
      style: {
        fill: UI_COLORS.mutedInk,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 22,
        fontWeight: '800'
      }
    });
    fileStatus.anchor.set(0.5);
    fileStatus.position.set(0, 92);
    fileCard.addChild(fileStatus);
    root.addChild(fileCard);

    const stampContainer = new Container();
    stampContainer.position.set(centerX, centerY);
    stampContainer.zIndex = 3;

    const stampMark = new Graphics();
    stampMark
      .roundRect(-258, -84, 516, 168, 10)
      .fill({ color: UI_COLORS.black, alpha: 0.03 })
      .stroke({ color: UI_COLORS.red, width: 13, alpha: 0.98 });
    stampMark
      .roundRect(-230, -56, 460, 112, 5)
      .stroke({ color: UI_COLORS.red, width: 5, alpha: 0.9 });
    stampMark.rect(-274, -10, 548, 20).fill({ color: UI_COLORS.redDark, alpha: 0.44 });
    stampMark.rect(-238, -76, 476, 5).fill({ color: UI_COLORS.red, alpha: 0.72 });
    stampMark.rect(-238, 72, 476, 5).fill({ color: UI_COLORS.red, alpha: 0.72 });
    stampContainer.addChild(stampMark);

    const stamp = new Text({
      text: label,
      style: {
        fill: UI_COLORS.red,
        fontFamily: UI_FONT_FAMILY,
        fontSize: label.length <= 4 ? 112 : 72,
        fontWeight: '900'
      }
    });
    stamp.anchor.set(0.5);
    stampContainer.addChild(stamp);
    stampContainer.rotation = -0.44;
    stampContainer.scale.set(1.42);
    root.addChild(stampContainer);

    const ledger = new Container();
    ledger.position.set(centerX, centerY + 144);
    ledger.zIndex = 4;

    const ledgerPlate = new Graphics();
    ledgerPlate
      .roundRect(-212, -26, 424, 52, 6)
      .fill({ color: UI_COLORS.redDark, alpha: 0.9 })
      .stroke({ color: UI_COLORS.gold, width: 2, alpha: 0.92 });
    ledger.addChild(ledgerPlate);

    const ledgerText = new Text({
      text: '未解決事件DB +1',
      style: {
        fill: UI_COLORS.ink,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 25,
        fontWeight: '900'
      }
    });
    ledgerText.anchor.set(0.5);
    ledger.addChild(ledgerText);
    root.addChild(ledger);

    this.stampDisplay = stampContainer;
    this.activeLineEffectKind = 'stamp';
    this.stampTimerMs = resolveTimedEffectDuration(options?.duration, STAMP_DURATION_MS);
    this.stampInputGuardMs = resolveEffectDuration(options?.inputGuard, STAMP_INPUT_GUARD_MS);
    this.stampElapsedMs = 0;
    this.stampSettleMs = resolveEffectDuration(options?.settle, STAMP_SETTLE_MS);
    this.startShake(
      resolveEffectIntensity(options?.shakeIntensity, 0.54),
      resolveEffectDuration(options?.shakeDuration, 320)
    );
  }

  private showKonaiArrival(options?: ScenarioLineEffectOptions): void {
    this.resetLineEffectLayer();

    const centerX = this.config.baseResolution.width / 2;
    const centerY = 408;
    const banner = new Container();
    banner.position.set(centerX, centerY);
    banner.rotation = -0.045;
    banner.scale.set(1.24);

    const backPlate = new Graphics();
    backPlate
      .roundRect(-330, -72, 660, 144, 10)
      .fill({ color: UI_COLORS.black, alpha: 0.92 })
      .stroke({ color: UI_COLORS.gold, width: 5, alpha: 0.94 });
    backPlate.rect(-350, -12, 700, 24).fill({ color: UI_COLORS.redDark, alpha: 0.9 });
    backPlate.rect(-292, 58, 584, 7).fill({ color: UI_COLORS.gold, alpha: 0.86 });
    banner.addChild(backPlate);

    const title = new Text({
      text: options?.title ?? '名迷探偵 コナイ',
      style: {
        fill: UI_COLORS.gold,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 54,
        fontWeight: '900'
      }
    });
    title.anchor.set(0.5);
    title.position.set(0, -18);
    banner.addChild(title);

    const subtitle = new Text({
      text: options?.subtitle ?? '現場に来た。解決は来ない。',
      style: {
        fill: UI_COLORS.ink,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 25,
        fontWeight: '800'
      }
    });
    subtitle.anchor.set(0.5);
    subtitle.position.set(0, 38);
    banner.addChild(subtitle);

    this.effectLayer.addChild(banner);
    this.activeLineEffectKind = 'konaiArrival';
    this.konaiArrivalTimerMs = resolveTimedEffectDuration(
      options?.duration,
      KONAI_ARRIVAL_DURATION_MS
    );
    this.konaiArrivalInputGuardMs = resolveEffectDuration(
      options?.inputGuard,
      KONAI_ARRIVAL_INPUT_GUARD_MS
    );
    this.konaiArrivalElapsedMs = 0;
    this.konaiArrivalSettleMs = resolveEffectDuration(options?.settle, KONAI_ARRIVAL_SETTLE_MS);
    this.startShake(
      resolveEffectIntensity(options?.shakeIntensity, 0.38),
      resolveEffectDuration(options?.shakeDuration, 220)
    );
  }

  private showPoliceCallCue(options?: ScenarioLineEffectOptions): void {
    this.resetLineEffectLayer();

    const cue = new Container();
    cue.position.set(this.config.baseResolution.width / 2, 168);
    cue.rotation = 0.035;
    cue.scale.set(1.16);

    const plate = new Graphics();
    plate
      .roundRect(-292, -58, 584, 116, 8)
      .fill({ color: UI_COLORS.black, alpha: 0.88 })
      .stroke({ color: UI_COLORS.red, width: 4, alpha: 0.9 });
    plate.rect(-276, -10, 552, 20).fill({ color: UI_COLORS.redDark, alpha: 0.72 });
    for (let index = 0; index < 5; index += 1) {
      plate.rect(-260, -42 + index * 20, 520, 2).fill({
        color: index % 2 === 0 ? UI_COLORS.gold : UI_COLORS.ink,
        alpha: 0.18
      });
    }
    cue.addChild(plate);

    const title = new Text({
      text: options?.title ?? '警部命令',
      style: {
        fill: UI_COLORS.red,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 32,
        fontWeight: '900'
      }
    });
    title.anchor.set(0.5);
    title.position.set(0, -26);
    cue.addChild(title);

    const command = new Text({
      text: options?.subtitle ?? '呼べ、コナイを！',
      style: {
        fill: UI_COLORS.ink,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 42,
        fontWeight: '900'
      }
    });
    command.anchor.set(0.5);
    command.position.set(0, 22);
    cue.addChild(command);

    this.effectLayer.addChild(cue);
    this.activeLineEffectKind = 'policeCall';
    this.policeCallTimerMs = resolveTimedEffectDuration(options?.duration, POLICE_CALL_DURATION_MS);
    this.policeCallInputGuardMs = resolveEffectDuration(
      options?.inputGuard,
      POLICE_CALL_INPUT_GUARD_MS
    );
    this.policeCallElapsedMs = 0;
    this.policeCallSettleMs = resolveEffectDuration(options?.settle, POLICE_CALL_SETTLE_MS);
    this.startShake(
      resolveEffectIntensity(options?.shakeIntensity, 0.28),
      resolveEffectDuration(options?.shakeDuration, 180)
    );
  }

  private showThiefNoticeCue(options?: ScenarioLineEffectOptions): void {
    this.resetLineEffectLayer();

    const cue = new Container();
    cue.position.set(this.config.baseResolution.width / 2, 318);
    cue.rotation = -0.055;
    cue.scale.set(1.18);

    const light = new Graphics();
    light.rect(-390, -72, 780, 34).fill({ color: UI_COLORS.redDark, alpha: 0.44 });
    light.rect(-350, 64, 700, 10).fill({ color: UI_COLORS.gold, alpha: 0.52 });
    cue.addChild(light);

    const paper = new Graphics();
    paper
      .roundRect(-274, -104, 548, 208, 8)
      .fill({ color: 0xf0e0c2, alpha: 0.96 })
      .stroke({ color: UI_COLORS.redDark, width: 4, alpha: 0.86 });
    paper.rect(-246, -72, 492, 12).fill({ color: UI_COLORS.redDark, alpha: 0.22 });
    paper.rect(-214, 70, 428, 5).fill({ color: UI_COLORS.black, alpha: 0.22 });
    cue.addChild(paper);

    const title = new Text({
      text: options?.title ?? '予告状',
      style: {
        fill: UI_COLORS.redDark,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 48,
        fontWeight: '900'
      }
    });
    title.anchor.set(0.5);
    title.position.set(0, -48);
    cue.addChild(title);

    const body = new Text({
      text: options?.subtitle ?? '盗む予定だけは一流',
      style: {
        fill: UI_COLORS.black,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 31,
        fontWeight: '900'
      }
    });
    body.anchor.set(0.5);
    body.position.set(0, 10);
    cue.addChild(body);

    const result = new Text({
      text: options?.statusText ?? '盗品実績: 0',
      style: {
        fill: UI_COLORS.red,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 27,
        fontWeight: '900'
      }
    });
    result.anchor.set(0.5);
    result.position.set(0, 60);
    cue.addChild(result);

    this.effectLayer.addChild(cue);
    this.activeLineEffectKind = 'thiefCue';
    this.thiefCueKind = 'notice';
    this.thiefCueTimerMs = resolveTimedEffectDuration(
      options?.duration,
      THIEF_CUE_NOTICE_DURATION_MS
    );
    this.thiefCueInputGuardMs = resolveEffectDuration(
      options?.inputGuard,
      THIEF_CUE_INPUT_GUARD_MS
    );
    this.thiefCueElapsedMs = 0;
    this.thiefCueSettleMs = resolveEffectDuration(options?.settle, THIEF_CUE_SETTLE_MS);
    this.startShake(
      resolveEffectIntensity(options?.shakeIntensity, 0.18),
      resolveEffectDuration(options?.shakeDuration, 150)
    );
  }

  private showThiefFailCue(options?: ScenarioLineEffectOptions): void {
    this.resetLineEffectLayer();

    const cue = new Container();
    cue.position.set(this.config.baseResolution.width / 2, 326);
    cue.rotation = 0.05;
    cue.scale.set(1.12);

    const plate = new Graphics();
    plate
      .roundRect(-238, -66, 476, 132, 10)
      .fill({ color: UI_COLORS.black, alpha: 0.9 })
      .stroke({ color: UI_COLORS.gold, width: 4, alpha: 0.88 });
    plate.rect(-260, -10, 520, 20).fill({ color: UI_COLORS.redDark, alpha: 0.7 });
    cue.addChild(plate);

    const title = new Text({
      text: options?.title ?? '盗品ゼロ',
      style: {
        fill: UI_COLORS.gold,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 50,
        fontWeight: '900'
      }
    });
    title.anchor.set(0.5);
    title.position.set(0, -18);
    cue.addChild(title);

    const subtitle = new Text({
      text: options?.subtitle ?? '予告状だけ散乱',
      style: {
        fill: UI_COLORS.ink,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 27,
        fontWeight: '900'
      }
    });
    subtitle.anchor.set(0.5);
    subtitle.position.set(0, 36);
    cue.addChild(subtitle);

    this.effectLayer.addChild(cue);
    this.activeLineEffectKind = 'thiefCue';
    this.thiefCueKind = 'fail';
    this.thiefCueTimerMs = resolveTimedEffectDuration(
      options?.duration,
      THIEF_CUE_FAIL_DURATION_MS
    );
    this.thiefCueInputGuardMs = resolveEffectDuration(
      options?.inputGuard,
      THIEF_CUE_INPUT_GUARD_MS
    );
    this.thiefCueElapsedMs = 0;
    this.thiefCueSettleMs = resolveEffectDuration(options?.settle, THIEF_CUE_SETTLE_MS);
    this.startShake(
      resolveEffectIntensity(options?.shakeIntensity, 0.22),
      resolveEffectDuration(options?.shakeDuration, 180)
    );
  }

  private showClockReverseCue(options?: ScenarioLineEffectOptions): void {
    this.resetLineEffectLayer();

    const centerX = this.config.baseResolution.width / 2;
    const centerY = 286;
    const root = new Container();
    root.sortableChildren = true;

    const dim = new Graphics();
    dim
      .rect(0, 0, this.config.baseResolution.width, this.config.baseResolution.height)
      .fill({ color: UI_COLORS.black, alpha: 0.28 });
    dim.rect(0, 0, this.config.baseResolution.width, 420).fill({
      color: UI_COLORS.redDark,
      alpha: 0.14
    });
    dim.zIndex = 0;
    root.addChild(dim);

    const panel = new Container();
    panel.position.set(centerX, centerY);
    panel.zIndex = 2;
    panel.scale.set(1.08);

    const backing = new Graphics();
    backing
      .roundRect(-286, -144, 572, 288, 10)
      .fill({ color: UI_COLORS.black, alpha: 0.88 })
      .stroke({ color: UI_COLORS.redDark, width: 4, alpha: 0.92 });
    backing.rect(-266, -120, 532, 5).fill({ color: UI_COLORS.gold, alpha: 0.58 });
    backing.rect(-266, 116, 532, 5).fill({ color: UI_COLORS.red, alpha: 0.42 });
    panel.addChild(backing);

    const title = new Text({
      text: options?.title ?? '時計塔異常',
      style: {
        fill: UI_COLORS.gold,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 28,
        fontWeight: '900'
      }
    });
    title.anchor.set(0.5);
    title.position.set(0, -112);
    panel.addChild(title);

    const clock = new Container();
    clock.position.set(0, -2);
    clock.zIndex = 4;

    const face = new Graphics();
    face
      .circle(0, 0, 86)
      .fill({ color: UI_COLORS.paperDark, alpha: 0.94 })
      .stroke({ color: UI_COLORS.gold, width: 5, alpha: 0.9 });
    face.circle(0, 0, 66).stroke({ color: UI_COLORS.redDark, width: 2, alpha: 0.7 });
    clock.addChild(face);

    for (let index = 0; index < 12; index += 1) {
      const tick = new Graphics();
      const tall = index % 3 === 0;
      tick.rect(-2, -78, 4, tall ? 18 : 10).fill({
        color: tall ? UI_COLORS.gold : UI_COLORS.ink,
        alpha: tall ? 0.88 : 0.58
      });
      tick.rotation = (Math.PI * 2 * index) / 12;
      clock.addChild(tick);
    }

    const shortHand = new Container();
    const shortHandGraphic = new Graphics();
    shortHandGraphic.roundRect(-5, -58, 10, 68, 4).fill({ color: UI_COLORS.gold, alpha: 0.94 });
    shortHand.addChild(shortHandGraphic);
    shortHand.rotation = 0.82;
    clock.addChild(shortHand);

    const longHand = new Container();
    const longHandGraphic = new Graphics();
    longHandGraphic.roundRect(-3, -74, 6, 82, 3).fill({ color: UI_COLORS.red, alpha: 0.96 });
    longHand.addChild(longHandGraphic);
    longHand.rotation = -0.22;
    clock.addChild(longHand);

    const centerPin = new Graphics();
    centerPin.circle(0, 0, 9).fill({ color: UI_COLORS.red, alpha: 0.98 });
    clock.addChild(centerPin);
    panel.addChild(clock);

    const subtitle = new Text({
      text: options?.subtitle ?? '針が戻る。記録だけが進む。',
      style: {
        fill: UI_COLORS.ink,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 24,
        fontWeight: '900'
      }
    });
    subtitle.anchor.set(0.5);
    subtitle.position.set(0, 112);
    panel.addChild(subtitle);

    root.addChild(panel);
    this.effectLayer.addChild(root);
    this.activeLineEffectKind = 'clockReverse';
    this.clockReverseDisplay = panel;
    this.clockReverseLongHand = longHand;
    this.clockReverseShortHand = shortHand;
    this.clockReverseTimerMs = resolveTimedEffectDuration(
      options?.duration,
      CLOCK_REVERSE_DURATION_MS
    );
    this.clockReverseInputGuardMs = resolveEffectDuration(
      options?.inputGuard,
      CLOCK_REVERSE_INPUT_GUARD_MS
    );
    this.clockReverseElapsedMs = 0;
    this.clockReverseDurationMs = this.clockReverseTimerMs;
    this.clockReverseSettleMs = resolveEffectDuration(options?.settle, CLOCK_REVERSE_SETTLE_MS);
    this.startShake(
      resolveEffectIntensity(options?.shakeIntensity, 0.24),
      resolveEffectDuration(options?.shakeDuration, 220)
    );
  }

  private showCaseFileTransitionCue(options?: ScenarioLineEffectOptions): void {
    this.resetLineEffectLayer();

    const centerX = this.config.baseResolution.width / 2;
    const centerY = 286;
    const root = new Container();
    root.sortableChildren = true;

    const wash = new Graphics();
    wash
      .rect(0, 0, this.config.baseResolution.width, this.config.baseResolution.height)
      .fill({ color: UI_COLORS.black, alpha: 0.22 });
    wash.rect(0, 82, this.config.baseResolution.width, 10).fill({
      color: UI_COLORS.redDark,
      alpha: 0.48
    });
    wash.rect(0, 430, this.config.baseResolution.width, 10).fill({
      color: UI_COLORS.gold,
      alpha: 0.28
    });
    wash.zIndex = 0;
    root.addChild(wash);

    const sheet = new Container();
    sheet.position.set(centerX + 290, centerY);
    sheet.rotation = 0.08;
    sheet.scale.set(1.08);
    sheet.zIndex = 3;

    const paper = new Graphics();
    paper
      .roundRect(-370, -146, 740, 292, 8)
      .fill({ color: 0xf1e3c5, alpha: 0.97 })
      .stroke({ color: UI_COLORS.gold, width: 3, alpha: 0.88 });
    paper.rect(-370, -146, 740, 46).fill({ color: UI_COLORS.black, alpha: 0.88 });
    paper.rect(-336, -74, 672, 4).fill({ color: UI_COLORS.redDark, alpha: 0.34 });
    paper.rect(-336, 66, 672, 4).fill({ color: UI_COLORS.black, alpha: 0.22 });
    for (let index = 0; index < 5; index += 1) {
      paper.rect(-318, 92 + index * 18, 560 - index * 28, 3).fill({
        color: UI_COLORS.black,
        alpha: 0.16
      });
    }
    sheet.addChild(paper);

    const kicker = new Text({
      text: options?.kicker ?? '未解決速報',
      style: {
        fill: UI_COLORS.gold,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 24,
        fontWeight: '900'
      }
    });
    kicker.anchor.set(0.5);
    kicker.position.set(0, -123);
    sheet.addChild(kicker);

    const headline = new Text({
      text: options?.title ?? '事件資料を更新',
      style: {
        fill: UI_COLORS.black,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 50,
        fontWeight: '900'
      }
    });
    headline.anchor.set(0.5);
    headline.position.set(0, -38);
    sheet.addChild(headline);

    const caseLine = new Text({
      text: options?.caseTitle ?? 'Case 0  消えた宝石商の娘',
      style: {
        fill: UI_COLORS.redDark,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 28,
        fontWeight: '900'
      }
    });
    caseLine.anchor.set(0.5);
    caseLine.position.set(0, 24);
    sheet.addChild(caseLine);

    const status = new Text({
      text: options?.statusText ?? '未解決事件リストへ追記',
      style: {
        fill: UI_COLORS.black,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 26,
        fontWeight: '900'
      }
    });
    status.anchor.set(0.5);
    status.position.set(0, 72);
    sheet.addChild(status);

    const stamp = new Container();
    stamp.position.set(248, 88);
    stamp.rotation = -0.16;
    const stampMark = new Graphics();
    stampMark
      .roundRect(-72, -28, 144, 56, 5)
      .stroke({ color: UI_COLORS.red, width: 4, alpha: 0.9 });
    stamp.addChild(stampMark);
    const stampText = new Text({
      text: options?.stampText ?? '未解決',
      style: {
        fill: UI_COLORS.red,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 26,
        fontWeight: '900'
      }
    });
    stampText.anchor.set(0.5);
    stamp.addChild(stampText);
    sheet.addChild(stamp);

    root.addChild(sheet);
    this.effectLayer.addChild(root);
    this.activeLineEffectKind = 'caseFileTransition';
    this.caseFileTransitionDisplay = sheet;
    this.caseFileTransitionTimerMs = resolveTimedEffectDuration(
      options?.duration,
      CASE_FILE_TRANSITION_DURATION_MS
    );
    this.caseFileTransitionInputGuardMs = resolveEffectDuration(
      options?.inputGuard,
      CASE_FILE_TRANSITION_INPUT_GUARD_MS
    );
    this.caseFileTransitionElapsedMs = 0;
    this.caseFileTransitionSettleMs = resolveEffectDuration(
      options?.settle,
      CASE_FILE_TRANSITION_SETTLE_MS
    );
    this.startShake(
      resolveEffectIntensity(options?.shakeIntensity, 0.12),
      resolveEffectDuration(options?.shakeDuration, 120)
    );
  }

  private clearStampState(): void {
    this.stampDisplay = null;
    this.stampTimerMs = 0;
    this.stampInputGuardMs = 0;
    this.stampElapsedMs = 0;
    this.stampSettleMs = STAMP_SETTLE_MS;
  }

  private clearKonaiArrivalState(): void {
    this.konaiArrivalTimerMs = 0;
    this.konaiArrivalInputGuardMs = 0;
    this.konaiArrivalElapsedMs = 0;
    this.konaiArrivalSettleMs = KONAI_ARRIVAL_SETTLE_MS;
  }

  private clearPoliceCallState(): void {
    this.policeCallTimerMs = 0;
    this.policeCallInputGuardMs = 0;
    this.policeCallElapsedMs = 0;
    this.policeCallSettleMs = POLICE_CALL_SETTLE_MS;
  }

  private clearThiefCueState(): void {
    this.thiefCueTimerMs = 0;
    this.thiefCueInputGuardMs = 0;
    this.thiefCueElapsedMs = 0;
    this.thiefCueSettleMs = THIEF_CUE_SETTLE_MS;
    this.thiefCueKind = null;
  }

  private clearClockReverseState(): void {
    this.clockReverseDisplay = null;
    this.clockReverseLongHand = null;
    this.clockReverseShortHand = null;
    this.clockReverseTimerMs = 0;
    this.clockReverseInputGuardMs = 0;
    this.clockReverseElapsedMs = 0;
    this.clockReverseDurationMs = CLOCK_REVERSE_DURATION_MS;
    this.clockReverseSettleMs = CLOCK_REVERSE_SETTLE_MS;
  }

  private clearCaseFileTransitionState(): void {
    this.caseFileTransitionDisplay = null;
    this.caseFileTransitionTimerMs = 0;
    this.caseFileTransitionInputGuardMs = 0;
    this.caseFileTransitionElapsedMs = 0;
    this.caseFileTransitionSettleMs = CASE_FILE_TRANSITION_SETTLE_MS;
  }

  private resetLineEffectLayer(): void {
    destroyChildren(this.effectLayer);
    this.effectLayer.alpha = 1;
    this.effectLayer.position.set(0, 0);
    this.effectLayer.scale.set(1);
    this.clearStampState();
    this.clearKonaiArrivalState();
    this.clearPoliceCallState();
    this.clearThiefCueState();
    this.clearClockReverseState();
    this.clearCaseFileTransitionState();
    this.activeLineEffectKind = null;
  }

  private updateClockReverseCue(deltaMs: number): void {
    this.clockReverseElapsedMs += deltaMs;
    this.clockReverseTimerMs = Math.max(0, this.clockReverseTimerMs - deltaMs);
    this.clockReverseInputGuardMs = Math.max(0, this.clockReverseInputGuardMs - deltaMs);

    const reduceMotion = this.isReduceMotionEnabled();
    const progress = Math.min(
      1,
      this.clockReverseElapsedMs / Math.max(1, this.clockReverseDurationMs)
    );
    const settleProgress = Math.min(
      1,
      this.clockReverseElapsedMs / Math.max(1, this.clockReverseSettleMs)
    );
    const easedSettle = 1 - Math.pow(1 - settleProgress, 3);
    const reverseSpin = reduceMotion ? 0 : progress * Math.PI * 5.2;
    if (this.clockReverseLongHand) {
      this.clockReverseLongHand.rotation = -0.22 - reverseSpin;
    }
    if (this.clockReverseShortHand) {
      this.clockReverseShortHand.rotation = 0.82 - reverseSpin * 0.22;
    }
    if (this.clockReverseDisplay) {
      if (reduceMotion) {
        this.clockReverseDisplay.scale.set(1);
        this.clockReverseDisplay.rotation = 0;
      } else {
        const jitter = Math.sin(this.clockReverseElapsedMs / 34) * 0.016;
        this.clockReverseDisplay.scale.set(1.08 - 0.08 * easedSettle + jitter);
        this.clockReverseDisplay.rotation = Math.sin(this.clockReverseElapsedMs / 48) * 0.012;
      }
    }

    this.effectLayer.alpha = 1;
  }

  private updateCaseFileTransitionCue(deltaMs: number): void {
    this.caseFileTransitionElapsedMs += deltaMs;
    this.caseFileTransitionTimerMs = Math.max(0, this.caseFileTransitionTimerMs - deltaMs);
    this.caseFileTransitionInputGuardMs = Math.max(
      0,
      this.caseFileTransitionInputGuardMs - deltaMs
    );

    if (this.caseFileTransitionDisplay) {
      if (this.isReduceMotionEnabled()) {
        this.caseFileTransitionDisplay.position.x = this.config.baseResolution.width / 2;
        this.caseFileTransitionDisplay.rotation = 0;
        this.caseFileTransitionDisplay.scale.set(1);
      } else {
        const progress = Math.min(
          1,
          this.caseFileTransitionElapsedMs / Math.max(1, this.caseFileTransitionSettleMs)
        );
        const easedProgress = 1 - Math.pow(1 - progress, 3);
        const drift = Math.sin(this.caseFileTransitionElapsedMs / 52) * 2;
        this.caseFileTransitionDisplay.position.x =
          this.config.baseResolution.width / 2 + (1 - easedProgress) * 290 + drift;
        this.caseFileTransitionDisplay.rotation = 0.08 - 0.08 * easedProgress;
        this.caseFileTransitionDisplay.scale.set(1.08 - 0.08 * easedProgress);
      }
    }

    this.effectLayer.alpha = 1;
  }

  private updateEvidencePopupCue(deltaMs: number): void {
    this.evidencePopupElapsedMs += deltaMs;
    this.evidencePopupTimerMs = Math.max(0, this.evidencePopupTimerMs - deltaMs);
    this.evidencePopupInputGuardMs = Math.max(0, this.evidencePopupInputGuardMs - deltaMs);

    if (this.evidencePopupDisplay) {
      const settleProgress = Math.min(
        1,
        this.evidencePopupElapsedMs / Math.max(1, this.evidencePopupSettleMs)
      );
      const easedSettle = 1 - Math.pow(1 - settleProgress, 3);
      this.evidencePopupDisplay.alpha = Math.min(1, easedSettle * 1.08);
      if (this.isReduceMotionEnabled()) {
        this.evidencePopupDisplay.scale.set(1);
      } else {
        const settleScale = 0.94 + 0.06 * easedSettle;
        const pulse = Math.sin(this.evidencePopupElapsedMs / 42) * 0.004;
        this.evidencePopupDisplay.scale.set(settleScale + pulse);
      }
    }
  }

  private updateThiefCue(deltaMs: number): void {
    this.thiefCueElapsedMs += deltaMs;
    this.thiefCueTimerMs = Math.max(0, this.thiefCueTimerMs - deltaMs);
    this.thiefCueInputGuardMs = Math.max(0, this.thiefCueInputGuardMs - deltaMs);
    const cue = this.effectLayer.children[0];
    if (cue) {
      const baseScale = this.thiefCueKind === 'notice' ? 1.18 : 1.12;
      const baseRotation = this.thiefCueKind === 'notice' ? -0.055 : 0.05;
      if (this.isReduceMotionEnabled()) {
        cue.scale.set(baseScale - 0.18);
        cue.rotation = baseRotation;
        cue.position.x = this.config.baseResolution.width / 2;
      } else {
        const progress = Math.min(1, this.thiefCueElapsedMs / Math.max(1, this.thiefCueSettleMs));
        const easedProgress = 1 - Math.pow(1 - progress, 3);
        const wobble = Math.sin(this.thiefCueElapsedMs / 44) * 0.014;
        cue.scale.set(baseScale - 0.18 * easedProgress + wobble);
        cue.rotation = baseRotation + Math.sin(this.thiefCueElapsedMs / 58) * 0.014;
        cue.position.x =
          this.config.baseResolution.width / 2 + Math.sin(this.thiefCueElapsedMs / 36) * 3;
      }
    }
    this.effectLayer.alpha = 1;
  }

  private updateEffects(deltaMs: number): void {
    this.transitionInputGuardMs = Math.max(0, this.transitionInputGuardMs - deltaMs);
    this.updateSceneFade(deltaMs);

    switch (this.activeLineEffectKind) {
      case 'clockReverse':
        this.updateClockReverseCue(deltaMs);
        break;
      case 'caseFileTransition':
        this.updateCaseFileTransitionCue(deltaMs);
        break;
      case 'thiefCue':
        this.updateThiefCue(deltaMs);
        break;
      case 'policeCall': {
        this.policeCallElapsedMs += deltaMs;
        this.policeCallTimerMs = Math.max(0, this.policeCallTimerMs - deltaMs);
        this.policeCallInputGuardMs = Math.max(0, this.policeCallInputGuardMs - deltaMs);
        const cue = this.effectLayer.children[0];
        if (cue) {
          if (this.isReduceMotionEnabled()) {
            cue.scale.set(1);
            cue.position.x = this.config.baseResolution.width / 2;
          } else {
            const progress = Math.min(
              1,
              this.policeCallElapsedMs / Math.max(1, this.policeCallSettleMs)
            );
            const easedProgress = 1 - Math.pow(1 - progress, 3);
            const jitter = Math.sin(this.policeCallElapsedMs / 30) * 0.012;
            cue.scale.set(1.16 - 0.16 * easedProgress + jitter);
            cue.position.x =
              this.config.baseResolution.width / 2 + Math.sin(this.policeCallElapsedMs / 24) * 4;
          }
        }
        this.effectLayer.alpha = 1;
        break;
      }
      case 'konaiArrival': {
        this.konaiArrivalElapsedMs += deltaMs;
        this.konaiArrivalTimerMs = Math.max(0, this.konaiArrivalTimerMs - deltaMs);
        this.konaiArrivalInputGuardMs = Math.max(0, this.konaiArrivalInputGuardMs - deltaMs);
        const banner = this.effectLayer.children[0];
        if (banner) {
          if (this.isReduceMotionEnabled()) {
            banner.scale.set(1);
            banner.rotation = -0.045;
          } else {
            const progress = Math.min(
              1,
              this.konaiArrivalElapsedMs / Math.max(1, this.konaiArrivalSettleMs)
            );
            const easedProgress = 1 - Math.pow(1 - progress, 3);
            const wobble = Math.sin(this.konaiArrivalElapsedMs / 38) * 0.018;
            banner.scale.set(1.24 - 0.24 * easedProgress + wobble);
            banner.rotation = -0.045 + Math.sin(this.konaiArrivalElapsedMs / 52) * 0.018;
          }
        }
        this.effectLayer.alpha = 1;
        break;
      }
      case 'stamp': {
        this.stampElapsedMs += deltaMs;
        this.stampTimerMs = Math.max(0, this.stampTimerMs - deltaMs);
        this.stampInputGuardMs = Math.max(0, this.stampInputGuardMs - deltaMs);
        const stampContainer = this.stampDisplay;
        if (stampContainer) {
          if (this.isReduceMotionEnabled()) {
            stampContainer.rotation = -0.14;
            stampContainer.scale.set(1.08);
          } else {
            const progress = Math.min(1, this.stampElapsedMs / Math.max(1, this.stampSettleMs));
            const easedProgress = 1 - Math.pow(1 - progress, 3);
            const impact = Math.sin(Math.min(1, this.stampElapsedMs / 120) * Math.PI) * 0.04;
            stampContainer.rotation = -0.44 + 0.3 * easedProgress;
            stampContainer.scale.set(1.42 - 0.34 * easedProgress + impact);
          }
        }
        this.effectLayer.alpha = 1;
        break;
      }
      case null:
        break;
    }

    if (this.evidencePopupDisplay) {
      this.updateEvidencePopupCue(deltaMs);
    }

    if (this.shakeTimerMs > 0) {
      if (this.isReduceMotionEnabled()) {
        this.shakeTimerMs = 0;
        this.container.position.set(0, 0);
        return;
      }

      this.shakeTimerMs = Math.max(0, this.shakeTimerMs - deltaMs);
      const phase = Math.ceil(this.shakeTimerMs / 34);
      const amount = this.shakeIntensity * 12;
      this.container.position.set(phase % 2 === 0 ? amount : -amount, phase % 3 === 0 ? -4 : 4);
      if (this.shakeTimerMs <= 0) {
        this.container.position.set(0, 0);
      }
    }
  }

  private updateSceneFade(deltaMs: number): void {
    if (!this.sceneFade) {
      return;
    }

    const fade = this.sceneFade;
    fade.elapsedMs += Math.max(0, deltaMs);
    const progress =
      fade.durationMs <= 0 ? 1 : Math.min(1, fade.elapsedMs / Math.max(1, fade.durationMs));
    const easedProgress = 1 - Math.pow(1 - progress, 3);
    let alpha: number;

    if (fade.direction === 'out') {
      alpha = interpolate(fade.startAlpha, 1, easedProgress);
    } else if (fade.direction === 'in') {
      alpha = interpolate(fade.startAlpha, 0, easedProgress);
    } else {
      alpha = Math.sin(progress * Math.PI);
    }

    this.drawFadeOverlay(fade.color, alpha);
    if (progress >= 1) {
      this.finishSceneFade();
    }
  }

  private finishSceneFade(): void {
    if (!this.sceneFade) {
      return;
    }

    const fade = this.sceneFade;
    const finalAlpha = fade.direction === 'out' ? 1 : 0;
    this.drawFadeOverlay(fade.color, finalAlpha);
    this.sceneFade = null;
    if (finalAlpha <= 0) {
      this.fadeLayer.visible = false;
      this.fadeLayer.eventMode = 'none';
    }
    fade.resolve();
    this.updateInputBlockState();
  }

  private drawFadeOverlay(color: number, alpha: number): void {
    const layout = this.layout;
    const width = layout?.baseWidth ?? this.config.baseResolution.width;
    const height = layout?.baseHeight ?? this.config.baseResolution.height;
    const clampedAlpha = Math.max(0, Math.min(alpha, 1));

    this.fadeOverlay.clear();
    this.fadeOverlay.alpha = clampedAlpha;
    if (clampedAlpha <= 0) {
      return;
    }

    this.fadeOverlay.rect(0, 0, width, height).fill({ color, alpha: 1 });
  }

  private isFadeBlocking(): boolean {
    return this.sceneFade !== null || (this.fadeLayer.visible && this.fadeOverlay.alpha > 0.98);
  }

  private isInputBlocked(): boolean {
    return this.createInputDebugState().blocked;
  }

  private updateInputBlockState(): void {
    const state = this.createInputDebugState();
    this.inputDebugState = state;
    this.syncEffectInputLock(state.blocked);
    this.updateInputDebugOverlay(this.inputDebugState);
    this.uiLayer.eventMode = state.blocked ? 'none' : 'passive';
    this.fadeLayer.eventMode = this.isFadeBlocking() ? 'static' : 'none';
  }

  private createInputDebugState(): NovelInputDebugState {
    const backgroundTransitioning = this.backgroundManager.getDebugState().transitioning;
    const fade = this.isFadeBlocking();
    const choicePanel = this.choicePanel.getState();
    const guards = {
      transition: Math.ceil(this.transitionInputGuardMs),
      backgroundTransitioning,
      caseFileTransition: Math.ceil(this.caseFileTransitionInputGuardMs),
      clockReverse: Math.ceil(this.clockReverseInputGuardMs),
      thiefCue: Math.ceil(this.thiefCueInputGuardMs),
      policeCall: Math.ceil(this.policeCallInputGuardMs),
      konaiArrival: Math.ceil(this.konaiArrivalInputGuardMs),
      stamp: Math.ceil(this.stampInputGuardMs),
      evidencePopup: Math.ceil(this.evidencePopupInputGuardMs),
      fade,
      completingNode: this.completingNode,
      choicePanelVisible: choicePanel.visible,
      choicePanelLocked: choicePanel.locked,
      choicePanelIntroComplete: choicePanel.introComplete
    };
    const reasons: string[] = [];
    if (guards.transition > 0) {
      reasons.push('backgroundTransitionGuard');
    }
    if (backgroundTransitioning) {
      reasons.push('backgroundTransitioning');
    }
    if (guards.caseFileTransition > 0) {
      reasons.push('caseFileTransition');
    }
    if (guards.clockReverse > 0) {
      reasons.push('clockReverse');
    }
    if (guards.thiefCue > 0) {
      reasons.push('thiefCue');
    }
    if (guards.policeCall > 0) {
      reasons.push('policeCall');
    }
    if (guards.konaiArrival > 0) {
      reasons.push('konaiArrival');
    }
    if (guards.stamp > 0) {
      reasons.push('stamp');
    }
    if (guards.evidencePopup > 0) {
      reasons.push('evidencePopup');
    }
    if (fade) {
      reasons.push('fade');
    }
    if (guards.completingNode) {
      reasons.push('completingNode');
    }

    return createNovelInputDebugState(
      reasons.length > 0,
      reasons,
      this.effectInputLockActive,
      guards,
      this.services.getInput().getDebugState(),
      this.playbackDebugState,
      this.createEffectDebugState(),
      this.backgroundManager.getDebugState(),
      this.characterManager.getDebugState()
    );
  }

  private createEffectDebugState(): NovelEffectDebugState {
    return {
      effectLayerChildCount: this.effectLayer.children.length,
      effectLayerAlpha: this.effectLayer.alpha,
      effectLayerX: this.effectLayer.x,
      effectLayerY: this.effectLayer.y,
      effectLayerScaleX: this.effectLayer.scale.x,
      effectLayerScaleY: this.effectLayer.scale.y,
      sceneX: this.container.x,
      sceneY: this.container.y,
      fadeLayerVisible: this.fadeLayer.visible,
      fadeOverlayAlpha: this.fadeOverlay.alpha,
      sceneFadeActive: this.sceneFade !== null,
      activeTimers: {
        stamp: Math.ceil(this.stampTimerMs),
        konaiArrival: Math.ceil(this.konaiArrivalTimerMs),
        policeCall: Math.ceil(this.policeCallTimerMs),
        thiefCue: Math.ceil(this.thiefCueTimerMs),
        clockReverse: Math.ceil(this.clockReverseTimerMs),
        caseFileTransition: Math.ceil(this.caseFileTransitionTimerMs),
        evidencePopup: Math.ceil(this.evidencePopupTimerMs),
        shake: Math.ceil(this.shakeTimerMs)
      }
    };
  }

  private syncEffectInputLock(blocked: boolean): void {
    if (blocked && !this.effectInputLockActive) {
      this.services.getInput().lock(NOVEL_EFFECT_INPUT_LOCK_REASON);
      this.effectInputLockActive = true;
      debugLogger.log('input', 'novel effect lock active', {
        runner: this.runner.getState()
      });
      this.inputDebugState = {
        ...this.inputDebugState,
        effectInputLockActive: true,
        input: this.services.getInput().getDebugState()
      };
      return;
    }

    if (!blocked && this.effectInputLockActive) {
      this.releaseEffectInputLock();
      this.inputDebugState = {
        ...this.inputDebugState,
        effectInputLockActive: false,
        input: this.services.getInput().getDebugState()
      };
    }
  }

  private releaseEffectInputLock(): void {
    if (!this.effectInputLockActive) {
      return;
    }

    this.services.getInput().unlock(NOVEL_EFFECT_INPUT_LOCK_REASON);
    this.effectInputLockActive = false;
    debugLogger.log('input', 'novel effect lock released', {
      runner: this.runner.getState()
    });
  }

  private updateInputDebugOverlay(state: NovelInputDebugState): void {
    if (!this.showInputDebugOverlay) {
      return;
    }

    const lockSummary = Object.entries(state.input.lockReasons)
      .map(([reason, count]) => `${reason}:${count}`)
      .join(', ');
    this.inputDebugText.text = state.blocked
      ? `INPUT LOCK ${state.reasons.join(', ')} / locks ${lockSummary || 'none'}`
      : `INPUT OK / locks ${lockSummary || 'none'}`;
  }

  private positionChoicePanel(choiceCount = this.choicePanel.getState().choiceCount): void {
    if (!this.layout) {
      return;
    }

    const panelHeight = getChoicePanelHeight(choiceCount);
    const x = Math.round((this.layout.baseWidth - CHOICE_PANEL_WIDTH) / 2);
    const y = Math.max(
      this.layout.safeArea.y + 76,
      this.textBox.y - panelHeight - CHOICE_PANEL_BOTTOM_MARGIN
    );
    this.choicePanel.position.set(x, Math.round(y));
  }

  private handleHeaderAction(
    action: 'save' | 'evidence' | 'caseList' | 'settings' | 'title'
  ): void {
    debugLogger.log('scene', 'novel header action', {
      action,
      blocked: this.isInputBlocked()
    });
    if (this.isInputBlocked()) {
      this.headerBar.releaseActionLock();
      return;
    }

    switch (action) {
      case 'save':
        this.saveProgressManually();
        break;
      case 'evidence':
        if (this.options.onOpenEvidence) {
          this.navigateAfterCurrentPointer(() => this.options.onOpenEvidence?.());
        } else {
          this.showNotice(
            '証拠品',
            'いまは証拠品画面を開けません。タイトルから再度お試しください。'
          );
        }
        break;
      case 'caseList':
        if (this.options.onOpenCaseList) {
          this.navigateAfterCurrentPointer(() => this.options.onOpenCaseList?.());
        } else {
          this.showNotice(
            '事件リスト',
            'いまは事件リストを開けません。タイトルから再度お試しください。'
          );
        }
        break;
      case 'settings':
        if (this.options.onOpenSettings) {
          this.navigateAfterCurrentPointer(() => this.options.onOpenSettings?.());
        } else {
          this.showNotice('設定', 'いまは設定画面を開けません。タイトルから再度お試しください。');
        }
        break;
      case 'title':
        this.confirmGoToTitle();
        break;
    }
  }

  private navigateAfterCurrentPointer(navigate: () => void): void {
    debugLogger.log('scene', 'navigate after current pointer');
    this.services.getInput().lock();
    try {
      navigate();
    } finally {
      window.setTimeout(() => this.services.getInput().unlock(), 0);
    }
  }

  private openBacklog(): void {
    if (this.isInputBlocked()) {
      debugLogger.log('scene', 'open backlog ignored by input block', this.inputDebugState);
      return;
    }

    this.services.getInput().lock();
    this.backlogModal.setEntries(this.services.getState().getBacklog());
    this.backlogModal.show();
    debugLogger.log('scene', 'open backlog', {
      backlogCount: this.services.getState().getBacklog().length
    });
  }

  private closeBacklog(): void {
    this.services.getInput().unlock();
    debugLogger.log('scene', 'close backlog');
  }

  private showNotice(title: string, message: string): void {
    debugLogger.log('scene', 'show notice', { title, message });
    this.destroyNoticeModal();
    this.services.getInput().lock();
    const layout = this.layout;
    const modal = new Modal({
      title,
      message,
      viewportWidth: layout?.baseWidth ?? this.config.baseResolution.width,
      viewportHeight: layout?.baseHeight ?? this.config.baseResolution.height,
      actions: [
        {
          label: '閉じる',
          variant: 'primary',
          onPress: () => this.destroyNoticeModal()
        }
      ],
      onDismiss: () => this.destroyNoticeModal()
    });
    this.noticeModal = modal;
    this.modalLayer.addChild(modal);
  }

  private confirmGoToTitle(): void {
    this.destroyNoticeModal();
    this.services.getInput().lock();
    const layout = this.layout;
    const modal = new Modal({
      title: 'タイトルへ戻る',
      message: '現在の進行を中断してタイトルへ戻ります。',
      viewportWidth: layout?.baseWidth ?? this.config.baseResolution.width,
      viewportHeight: layout?.baseHeight ?? this.config.baseResolution.height,
      actions: [
        {
          label: 'キャンセル',
          variant: 'ghost',
          onPress: () => this.destroyNoticeModal()
        },
        {
          label: '戻る',
          variant: 'danger',
          onPress: () => {
            this.destroyNoticeModal();
            this.goToTitle();
          }
        }
      ],
      onDismiss: () => this.destroyNoticeModal()
    });
    this.noticeModal = modal;
    this.modalLayer.addChild(modal);
  }

  private destroyNoticeModal(): void {
    if (!this.noticeModal) {
      return;
    }

    this.noticeModal.destroy({ children: true });
    this.noticeModal = null;
    this.services.getInput().unlock();
    this.headerBar.releaseActionLock();
    debugLogger.log('scene', 'destroy notice');
  }

  private goToTitle(): void {
    this.leavingScene = true;
    debugLogger.log('scene', 'novel go to title', {
      runner: this.runner.getState()
    });
    this.services.getInput().unlock();
    this.options.onGoToTitle?.();
  }

  private async completeCaseAndContinue(): Promise<void> {
    this.leavingScene = true;
    debugLogger.log('scene', 'novel complete case', {
      runner: this.runner.getState()
    });
    this.services.getInput().unlock();

    if (this.options.onCompleteCase) {
      await this.options.onCompleteCase();
      return;
    }

    this.options.onGoToTitle?.();
  }

  private async handleInput(event: GameInputEvent): Promise<void> {
    debugLogger.log('input', 'novel handle input', {
      event,
      blocked: this.isInputBlocked(),
      noticeVisible: this.noticeModal !== null,
      backlogVisible: this.backlogModal.visible
    });
    if (!this.noticeModal && !this.backlogModal.visible && this.isInputBlocked()) {
      return;
    }

    if (event.command === 'primary') {
      this.resetAutoAdvanceTimer();
      await this.advance();
    } else if (event.command === 'menu') {
      this.openBacklog();
    } else if (event.command === 'cancel' && this.noticeModal) {
      this.destroyNoticeModal();
    }
  }
}

function getChoicePanelHeight(choiceCount: number): number {
  if (choiceCount <= 0) {
    return CHOICE_PANEL_HEADER_HEIGHT;
  }

  return (
    CHOICE_PANEL_HEADER_HEIGHT +
    choiceCount * CHOICE_BUTTON_HEIGHT +
    (choiceCount - 1) * CHOICE_BUTTON_GAP
  );
}

function createNovelInputDebugState(
  blocked: boolean,
  reasons: string[],
  effectInputLockActive: boolean,
  guards: NovelInputDebugState['guards'],
  input: InputDebugState = createEmptyInputDebugState(),
  playback: NovelPlaybackDebugState = createNovelPlaybackDebugState(false, false, false, 'normal'),
  effects: NovelEffectDebugState = createEmptyNovelEffectDebugState(),
  background: BackgroundDebugState = createEmptyBackgroundDebugState(),
  characters: CharacterManagerDebugState = createEmptyCharacterDebugState()
): NovelInputDebugState {
  return {
    blocked,
    reasons: [...reasons],
    effectInputLockActive,
    guards: { ...guards },
    playback: { ...playback },
    effects: {
      ...effects,
      activeTimers: { ...effects.activeTimers }
    },
    background: { ...background },
    characters: {
      characterCount: characters.characterCount,
      characters: characters.characters.map((character) => ({ ...character }))
    },
    input: {
      ...input,
      lockReasons: { ...input.lockReasons }
    }
  };
}

function cloneNovelInputDebugState(state: NovelInputDebugState): NovelInputDebugState {
  return createNovelInputDebugState(
    state.blocked,
    state.reasons,
    state.effectInputLockActive,
    state.guards,
    state.input,
    state.playback,
    state.effects,
    state.background,
    state.characters
  );
}

function createNovelPlaybackDebugState(
  fastEffects: boolean,
  suppressSe: boolean,
  revealTextInstantly: boolean,
  reason: LinePlaybackReason
): NovelPlaybackDebugState {
  return {
    fastEffects,
    suppressSe,
    revealTextInstantly,
    reason
  };
}

function createEmptyInputDebugState(): InputDebugState {
  return {
    enabled: true,
    locked: false,
    lockDepth: 0,
    lockReasons: {},
    lastCommand: null,
    lastSource: null,
    emittedCount: 0,
    ignoredDisabledCount: 0,
    ignoredLockedCount: 0,
    ignoredThrottledCount: 0,
    lastIgnoredReason: null,
    lastIgnoredCommand: null,
    lastIgnoredSource: null,
    listenerCount: 0
  };
}

function createEmptyBackgroundDebugState(): BackgroundDebugState {
  return {
    currentBackgroundId: null,
    label: null,
    category: null,
    known: false,
    currentEffect: 'none',
    currentEffectIntensity: 0,
    transitioning: false,
    placeholder: true,
    backgroundLayerChildCount: 0,
    currentLayerChildCount: 0,
    transitionPreviousChildCount: 0,
    effectLayerChildCount: 0,
    effectLayerAlpha: 1,
    effectLayerVisible: true,
    effectLayerX: 0,
    effectLayerY: 0,
    backgroundLayerScaleX: 1,
    backgroundLayerScaleY: 1,
    backgroundLayerX: 0,
    backgroundLayerY: 0,
    containerX: 0,
    containerY: 0,
    rainLayerCount: 0,
    noiseFrameCount: 0,
    glitchLayerCount: 0,
    shakeActive: false,
    shakeTimerMs: 0,
    effectTimeMs: 0
  };
}

function createEmptyNovelEffectDebugState(): NovelEffectDebugState {
  return {
    effectLayerChildCount: 0,
    effectLayerAlpha: 1,
    effectLayerX: 0,
    effectLayerY: 0,
    effectLayerScaleX: 1,
    effectLayerScaleY: 1,
    sceneX: 0,
    sceneY: 0,
    fadeLayerVisible: false,
    fadeOverlayAlpha: 0,
    sceneFadeActive: false,
    activeTimers: {
      stamp: 0,
      konaiArrival: 0,
      policeCall: 0,
      thiefCue: 0,
      clockReverse: 0,
      caseFileTransition: 0,
      evidencePopup: 0,
      shake: 0
    }
  };
}

function createEmptyCharacterDebugState(): CharacterManagerDebugState {
  return {
    characterCount: 0,
    characters: []
  };
}

function shouldShowInputDebugOverlay(): boolean {
  if (!import.meta.env.DEV || typeof window === 'undefined') {
    return false;
  }

  return new URLSearchParams(window.location.search).get(INPUT_DEBUG_QUERY_PARAM) === '1';
}

function shouldUseFastEffectMode(): boolean {
  if (!import.meta.env.DEV || typeof window === 'undefined') {
    return false;
  }

  return new URLSearchParams(window.location.search).get(FAST_EFFECT_QUERY_PARAM) === '1';
}

function reduceBackgroundMotion(background: BackgroundCue): BackgroundCue {
  switch (background.effect) {
    case 'shake':
    case 'zoomIn':
    case 'zoomOut':
    case 'slowPan':
    case 'subtleDrift':
      return {
        ...background,
        effect: 'none'
      };
    case 'glitch':
      return {
        ...background,
        effect: 'noise',
        effectIntensity: reduceEffectIntensity(background.effectIntensity, 0.28)
      };
    case 'rain':
    case 'noise':
    case 'staticNoise':
      return {
        ...background,
        effectIntensity: reduceEffectIntensity(background.effectIntensity, 0.35)
      };
    case 'none':
    case 'darken':
    case undefined:
    default:
      return background;
  }
}

function applyLightweightBackgroundProfile(background: BackgroundCue): BackgroundCue {
  switch (background.effect) {
    case 'shake':
    case 'zoomIn':
    case 'zoomOut':
    case 'slowPan':
    case 'subtleDrift':
      return {
        ...background,
        effect: 'none'
      };
    case 'glitch':
      return {
        ...background,
        effect: 'noise',
        effectIntensity: reduceEffectIntensity(background.effectIntensity, 0.18)
      };
    case 'staticNoise':
      return {
        ...background,
        effect: 'noise',
        effectIntensity: reduceEffectIntensity(background.effectIntensity, 0.18)
      };
    case 'rain':
      return {
        ...background,
        effectIntensity: reduceEffectIntensity(background.effectIntensity, 0.2)
      };
    case 'noise':
      return {
        ...background,
        effectIntensity: reduceEffectIntensity(background.effectIntensity, 0.18)
      };
    case 'none':
    case 'darken':
    case undefined:
    default:
      return background;
  }
}

function reduceEffectIntensity(value: number | undefined, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    return fallback;
  }

  return Math.min(value, fallback);
}

function getEffectDurationDefaults(effect: ScenarioLineEffect | undefined): EffectDurationDefaults {
  switch (effect) {
    case 'shake':
      return {
        shakeDuration: 260
      };
    case 'stamp':
    case 'flash':
      return {
        duration: STAMP_DURATION_MS,
        inputGuard: STAMP_INPUT_GUARD_MS,
        settle: STAMP_SETTLE_MS,
        fade: STAMP_FADE_MS,
        shakeDuration: 320
      };
    case 'konaiArrival':
      return {
        duration: KONAI_ARRIVAL_DURATION_MS,
        inputGuard: KONAI_ARRIVAL_INPUT_GUARD_MS,
        settle: KONAI_ARRIVAL_SETTLE_MS,
        fade: KONAI_ARRIVAL_FADE_MS,
        shakeDuration: 220
      };
    case 'policeCall':
      return {
        duration: POLICE_CALL_DURATION_MS,
        inputGuard: POLICE_CALL_INPUT_GUARD_MS,
        settle: POLICE_CALL_SETTLE_MS,
        fade: POLICE_CALL_FADE_MS,
        shakeDuration: 180
      };
    case 'thiefNotice':
      return {
        duration: THIEF_CUE_NOTICE_DURATION_MS,
        inputGuard: THIEF_CUE_INPUT_GUARD_MS,
        settle: THIEF_CUE_SETTLE_MS,
        fade: THIEF_CUE_FADE_MS,
        shakeDuration: 150
      };
    case 'thiefFail':
      return {
        duration: THIEF_CUE_FAIL_DURATION_MS,
        inputGuard: THIEF_CUE_INPUT_GUARD_MS,
        settle: THIEF_CUE_SETTLE_MS,
        fade: THIEF_CUE_FADE_MS,
        shakeDuration: 180
      };
    case 'clockReverse':
      return {
        duration: CLOCK_REVERSE_DURATION_MS,
        inputGuard: CLOCK_REVERSE_INPUT_GUARD_MS,
        settle: CLOCK_REVERSE_SETTLE_MS,
        fade: CLOCK_REVERSE_FADE_MS,
        shakeDuration: 220
      };
    case 'caseFileTransition':
      return {
        duration: CASE_FILE_TRANSITION_DURATION_MS,
        inputGuard: CASE_FILE_TRANSITION_INPUT_GUARD_MS,
        settle: CASE_FILE_TRANSITION_SETTLE_MS,
        fade: CASE_FILE_TRANSITION_FADE_MS,
        shakeDuration: 120
      };
    case 'none':
    case 'glitch':
    case 'noise':
    case undefined:
    default:
      return {};
  }
}

function scaleEffectDuration(
  durationMs: number,
  key: EffectDurationOptionKey,
  scale: number
): number {
  const minimum = getFastEffectMinimumDuration(key);
  return Math.max(minimum, Math.round(durationMs * scale));
}

function getFastEffectMinimumDuration(key: EffectDurationOptionKey): number {
  switch (key) {
    case 'inputGuard':
      return FAST_EFFECT_MIN_INPUT_GUARD_MS;
    case 'settle':
      return FAST_EFFECT_MIN_SETTLE_MS;
    case 'fade':
      return FAST_EFFECT_MIN_FADE_MS;
    case 'shakeDuration':
      return FAST_EFFECT_MIN_SHAKE_MS;
    case 'duration':
    default:
      return FAST_EFFECT_MIN_DURATION_MS;
  }
}

function resolveEffectDuration(value: number | undefined, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    return fallback;
  }

  return Math.round(value);
}

function resolveTimedEffectDuration(value: number | undefined, fallback: number): number {
  return Math.max(1, resolveEffectDuration(value, fallback));
}

function resolveEffectIntensity(value: number | undefined, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback;
  }

  return Math.max(0, Math.min(value, 1));
}

function getStampLabel(text: string): string {
  return Array.from(text).length <= 8 ? text : '!';
}

function getCaseLabel(caseId: string): string {
  const match = /^case(\d+)$/i.exec(caseId);
  return match ? `Case ${match[1]}` : '事件ファイル';
}

function createAutoSavePositionKey(position: ScenarioResumePoint): string {
  return [position.scenarioId, position.nodeId, position.lineIndex, position.lineId ?? ''].join(
    ':'
  );
}

function formatSavedAt(value: string | undefined): string {
  if (!value) {
    return '保存時刻不明';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('ja-JP', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).format(date);
}

function parseFadeColor(color: string): number {
  const normalized = color.trim().replace(/^#/, '').replace(/^0x/i, '');
  if (/^[0-9a-f]{3}$/i.test(normalized)) {
    return Number.parseInt(
      normalized
        .split('')
        .map((value) => `${value}${value}`)
        .join(''),
      16
    );
  }

  if (/^[0-9a-f]{6}$/i.test(normalized)) {
    return Number.parseInt(normalized, 16);
  }

  return UI_COLORS.black;
}

function getInitialSceneFadeAlpha(
  direction: FadeRequest['direction'],
  currentAlpha: number
): number {
  if (direction === 'in') {
    return currentAlpha > 0 ? currentAlpha : 1;
  }

  if (direction === 'out') {
    return currentAlpha;
  }

  return 0;
}

function interpolate(from: number, to: number, progress: number): number {
  return from + (to - from) * progress;
}

function fitSpriteToBox(sprite: Sprite, maxWidth: number, maxHeight: number): void {
  const textureWidth = Math.max(1, sprite.texture.width);
  const textureHeight = Math.max(1, sprite.texture.height);
  const scale = Math.min(maxWidth / textureWidth, maxHeight / textureHeight);
  sprite.width = textureWidth * scale;
  sprite.height = textureHeight * scale;
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max));
}

function destroyChildren(container: Container): void {
  for (const child of container.removeChildren()) {
    child.destroy({ children: true });
  }
}
